// Foundation Analysis Input Form Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, Typography, TextField, Button,
  Grid, FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, Divider, Alert, CircularProgress, Box, Tooltip, Collapse,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Engineering, Calculate, Download } from '@mui/icons-material';
import axios from 'axios';
import FoundationLayoutViewer from './FoundationLayoutViewer';
import CalculationHistory from '../shared/CalculationHistory';
import {
  DEFAULT_GROUND_SLAB_FOUNDATION,
  DEFAULT_SLAB_OPTIONS,
  applySupportLayersToProfile,
  buildSlabFoundationWithLayout,
  meshNodeCounts,
  slabLoadsForAnalysis,
  stripSupportLayers,
  supportProfilesEqual,
} from './groundSlabHelpers';

// Bump when the shipped soil profile changes, so a saved copy of the old default is replaced once.
const SOIL_PROFILE_VERSION = 2;

const DEFAULT_SAND_LAYER = {
  soil_type: 'sand_medium',
  top_depth: 0,
  bottom_depth: 10,
  unit_weight: 18,
  cohesion: 0,
  friction_angle: 32,
  modulus: 30.0,
  E_modulus: 30.0,
  alpha_factor: 0.0,
  beta_factor: 0.4,
  is_settling: false,
  N60: '',
  fines_content_pct: '',
  is_saturated: false,
};

function bedrockLayerFrom(topDepth) {
  const top = Number(topDepth) || 0;
  return {
    name: 'bedrock',
    soil_type: 'bedrock',
    top_depth: top,
    bottom_depth: top + 8,
    unit_weight: 27.0,
    cohesion: 0,
    friction_angle: 50,
    modulus: 5000.0,
    E_modulus: 5000.0,
    is_settling: false,
    N60: '',
    fines_content_pct: '',
    is_saturated: false,
  };
}

const DEFAULT_SOIL_LAYERS = [
  DEFAULT_SAND_LAYER,
  bedrockLayerFrom(DEFAULT_SAND_LAYER.bottom_depth),
];

function isSupportCap(layer) {
  return layer?.support_role === 'gravel' || layer?.support_role === 'xps';
}

function isLegacyDefaultSandProfile(layers) {
  const natural = (layers || []).filter((layer) => !isSupportCap(layer));
  if (natural.length !== 1) return false;
  const layer = natural[0];
  const thickness = Number(layer.bottom_depth) - Number(layer.top_depth);
  return layer?.soil_type === 'sand_medium' && Math.abs(thickness - 2) < 0.02;
}

function withDefaultBedrock(layers) {
  if (!Array.isArray(layers) || layers.length === 0) return DEFAULT_SOIL_LAYERS;
  const natural = layers.filter((layer) => !isSupportCap(layer));
  if (natural.length === 1 && layers.length === 1) return DEFAULT_SOIL_LAYERS;
  const sand = natural[0];
  const rock = bedrockLayerFrom(sand.bottom_depth);
  const index = layers.indexOf(sand);
  const next = [...layers];
  next.splice(index + 1, 0, rock);
  const shift = rock.bottom_depth - rock.top_depth;
  for (let i = index + 2; i < next.length; i += 1) {
    next[i] = {
      ...next[i],
      top_depth: Number(next[i].top_depth) + shift,
      bottom_depth: Number(next[i].bottom_depth) + shift,
    };
  }
  return next;
}

function soilLayersFromSaved(saved) {
  const layers = saved?.soilLayers;
  if (!Array.isArray(layers) || layers.length === 0) return DEFAULT_SOIL_LAYERS;
  if ((saved.soilProfileVersion || 1) < SOIL_PROFILE_VERSION && isLegacyDefaultSandProfile(layers)) {
    return withDefaultBedrock(layers);
  }
  return layers;
}

const FoundationInputForm = ({ onAnalysisComplete }) => {
  console.log('=== FoundationInputForm COMPONENT RENDERED ===');
  const [searchParams, setSearchParams] = useSearchParams();
  const historyRef = useRef(null);

  // Helper to parse float with either comma or dot
  const parseFlexibleFloat = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const str = String(value).replace(',', '.');
    const val = parseFloat(str);
    return isNaN(val) ? 0 : val;
  };

  /** Hide only standard pile foundations with exactly 3 piles from pick lists. */
  const isHiddenStandardFoundation = (foundation) => Number(foundation?.num_piles || 0) === 3;
  const filterStandardFoundationsForUi = (list) => (list || []).filter((f) => f && !isHiddenStandardFoundation(f));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [standardFoundations, setStandardFoundations] = useState([]);
  const [showAdvancedFixity, setShowAdvancedFixity] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Storage key for foundation form data
  const STORAGE_KEY = 'foundation_input_data';

  // Function to save form data to localStorage
  const saveFormData = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save form data to localStorage:', e);
    }
  };

  // Function to load form data from localStorage
  const loadFormData = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.warn('Failed to load form data from localStorage:', e);
      return null;
    }
  };

  // Project data - load from localStorage if available
  const [projectData, setProjectData] = useState(() => {
    const saved = loadFormData();
    const defaults = {
      name: 'Foundation Analysis Project',
      description: '',
      foundation_type: 'column',
      seismic_enabled: false,
      seismic_params: {
        a_gR: 0.1,
        spectrum_type: 'Type1',
        ground_type: 'auto',
        importance_class: 'II',
        behavior_factor_q: 1.5,
        structural_system: 'other_DCL',
        building_height_m: 12,
        ductility_class: 'DCL',
        earthquake_magnitude_Mw: 6.5,
        structural_overstrength_omega: 1.0,
        enable_liquefaction_check: true,
        enable_kinematic_check: true,
        enable_mononobe_okabe: true,
        enable_capacity_design: true,
        enable_distributed_check: false,
        distributed_solver_nodes: 201,
        distributed_include_p_delta: false,
      },
      tension_resistance_enabled: false,
      embedment_depth: 0.0,
      pile_head_fixity: 0.0,
      pile_head_fixity_mode: 'preset',
      pile_head_fixity_preset: 'pinned',
      pile_head_fixity_computed: {
        cap_embedment_m: 0,
        top_rebar_development: 'none',
        cap_thickness_m: 0,
      },
      groundwater_depth: '',
      passive_mobilisation: 'hyperbolic',
      allow_piled_soil_bearing: false,
      passive_ref_displacement_m: '',
      passive_ref_ratio_h: 0.02,
      check_column_punching: false,
      column_side_x_m: 0.4,
      column_side_y_m: 0.4,
    };
    if (!saved?.projectData) return defaults;
    return {
      ...defaults,
      ...saved.projectData,
      seismic_params: {
        ...defaults.seismic_params,
        ...(saved.projectData.seismic_params || {}),
      },
    };
  });


  // Foundation selection - load from localStorage if available
  const [foundationType, setFoundationType] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.foundationType : 'column';
  });
  const [selectedFoundation, setSelectedFoundation] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.selectedFoundation : null;
  });
  const getComputedFixity = useCallback(() => {
    const c = projectData.pile_head_fixity_computed || {};
    const capEmbed = Math.max(0, parseFlexibleFloat(c.cap_embedment_m));
    const capThk = Math.max(0, parseFlexibleFloat(c.cap_thickness_m));
    const rebarMap = { none: 0, partial: 0.5, full: 1 };
    const sRebar = rebarMap[(c.top_rebar_development || 'none')] ?? 0;
    const pileD = ((selectedFoundation?.pile_size || 250) / 1000) || 0.25;
    const sEmbed = Math.max(0, Math.min(1, capEmbed / (1.5 * pileD)));
    const sCap = Math.max(0, Math.min(1, capThk / (1.5 * pileD)));
    return Math.max(0, Math.min(1, Math.min(sEmbed, sRebar) * sCap));
  }, [projectData.pile_head_fixity_computed, selectedFoundation?.pile_size]);

  // Flag to prevent circular updates between foundation type and selection
  const [isProgrammaticTypeChange, setIsProgrammaticTypeChange] = useState(false);
  // Ref to store latest pileData from FoundationLayoutViewer for synchronous access
  const latestPileDataRef = useRef(null);
  const [pileLength, setPileLength] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.pileLength : 12.0;
  });
  const [isCustomFoundation, setIsCustomFoundation] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.isCustomFoundation : false;
  });
  const [customFoundationName, setCustomFoundationName] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.customFoundationName : '';
  });
  const [ptlClass, setPtlClass] = useState(() => {
    const saved = loadFormData();
    return saved ? (saved.ptlClass || saved.selectedFoundation?.ptl_class || 2) : (selectedFoundation?.ptl_class || 2);
  });

  const normalizeSoilLayerModulus = (layer) => {
    if (!layer) return layer;
    const raw = layer.E_modulus ?? layer.modulus;
    if (raw == null || raw === '') return layer;
    const e = Number(raw);
    if (!Number.isFinite(e)) return layer;
    const soilType = String(layer.soil_type || layer.name || '').toLowerCase();
    const isRock = soilType.includes('bedrock') || soilType.includes('rock');
    let mpa = e;
    if (isRock) {
      // Rock is entered in MPa (hard bedrock is 5000). Values above 100000 are legacy kPa.
      // An earlier conversion divided every value above 500 by 1000, so 5000 MPa became 5.
      if (e >= 100000) mpa = e / 1000;
      else if (e < 100) mpa = e * 1000;
    } else if (e > 500) {
      // Catalogue soils stay under 500 MPa. Larger numbers are legacy kPa (e.g. 30000).
      mpa = e / 1000;
    }
    return { ...layer, E_modulus: mpa, modulus: mpa };
  };

  // Soil layers - load from localStorage if available
  const [soilLayers, setSoilLayers] = useState(() => {
    const saved = loadFormData();
    const layers = saved ? soilLayersFromSaved(saved) : DEFAULT_SOIL_LAYERS;
    return layers.map(normalizeSoilLayerModulus);
  });

  // Loads - load from localStorage if available
  const [loads, setLoads] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.loads : {
      vertical_load: 1000,
      horizontal_load_y: 50,
      horizontal_load_x: 0,
      moment_y: 100,
      moment_x: 0,
      moment_z: 0
    };
  });

  // Rebar area - load from localStorage if available
  const [rebarArea, setRebarArea] = useState(() => {
    const saved = loadFormData();
    return saved ? saved.rebarArea : 0;
  });

  const [slabOptions, setSlabOptions] = useState(() => {
    const saved = loadFormData();
    if (!saved?.slabOptions) return { ...DEFAULT_SLAB_OPTIONS };
    const s = saved.slabOptions;
    const concentrated = {
      ...DEFAULT_SLAB_OPTIONS.loads.concentrated,
      ...(s.loads?.concentrated || {}),
    };
    // Older builds turned a 20 kN patch on automatically and stored that in the browser.
    if (!s.loadsVersion && concentrated.enabled !== false && Number(concentrated.force_kn) === 20 && Number(concentrated.patch_mm) === 200) {
      concentrated.enabled = false;
    }
    return {
      ...DEFAULT_SLAB_OPTIONS,
      ...s,
      gravel: { ...DEFAULT_SLAB_OPTIONS.gravel, ...(s.gravel || {}) },
      xps: { ...DEFAULT_SLAB_OPTIONS.xps, ...(s.xps || {}) },
      pile_grid: { ...DEFAULT_SLAB_OPTIONS.pile_grid, ...(s.pile_grid || {}) },
      mesh: {
        spacing_m: s.mesh?.spacing_m ?? DEFAULT_SLAB_OPTIONS.mesh.spacing_m,
      },
      loads: {
        ...DEFAULT_SLAB_OPTIONS.loads,
        ...(s.loads || {}),
        concentrated,
        line: { ...DEFAULT_SLAB_OPTIONS.loads.line, ...(s.loads?.line || {}) },
      },
      loadsVersion: s.loadsVersion || 2,
      design_basis: { ...DEFAULT_SLAB_OPTIONS.design_basis, ...(s.design_basis || {}) },
    };
  });

  useEffect(() => {
    setSoilLayers((prev) => {
      const next = foundationType === 'ground_facing_slab'
        ? applySupportLayersToProfile(prev, slabOptions)
        : stripSupportLayers(prev);
      return supportProfilesEqual(prev, next) ? prev : next;
    });
  }, [
    foundationType,
    slabOptions.gravel?.enabled,
    slabOptions.gravel?.thickness_m,
    slabOptions.xps?.enabled,
    slabOptions.xps?.thickness_m,
  ]);

  const getFoundationSavePayload = useCallback(() => {
    const ft = foundationType === 'wall' ? 'wall'
      : foundationType === 'spread_footing' ? 'spread_footing'
      : foundationType === 'ground_facing_slab' ? 'ground_facing_slab'
      : foundationType === 'custom' ? 'custom' : 'column';
    return {
      name: `${projectData.name} — ${new Date().toLocaleString()}`,
      description: projectData.description || '',
      input_data: {
        projectData,
        foundationType,
        selectedFoundation,
        pileLength,
        soilLayers,
        loads,
        rebarArea,
        slabOptions,
      },
      results_data: results,
      foundation_type: ft,
      pile_type: selectedFoundation?.code || '',
      num_piles: foundationType === 'spread_footing' ? 0
        : foundationType === 'ground_facing_slab' ? (selectedFoundation?.num_piles ?? 0)
        : (selectedFoundation?.num_piles ?? 0),
      status: results ? 'completed' : 'draft',
    };
  }, [
    projectData,
    foundationType,
    selectedFoundation,
    pileLength,
    soilLayers,
    loads,
    rebarArea,
    slabOptions,
    results,
  ]);

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`/api/foundations/saved-analyses/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const analysisData = r.data;
        if (analysisData.input_data) {
          const inputData = analysisData.input_data;
          setProjectData((prev) => ({ ...prev, ...(inputData.projectData || {}) }));
          setFoundationType(inputData.foundationType || 'column');
          setSelectedFoundation(inputData.selectedFoundation || null);
          setPileLength(inputData.pileLength ?? 12.0);
          setSoilLayers(inputData.soilLayers || soilLayers);
          setLoads(inputData.loads || loads);
          setRebarArea(inputData.rebarArea ?? 0);
          if (inputData.slabOptions) setSlabOptions(inputData.slabOptions);
        }
        if (analysisData.results_data) {
          setResults(analysisData.results_data);
          onAnalysisComplete?.(analysisData.results_data);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const soilTypes = [
    { value: 'clay_soft', label: 'Soft Clay' },
    { value: 'clay_stiff', label: 'Stiff Clay' },
    { value: 'sand_loose', label: 'Loose Sand' },
    { value: 'sand_medium', label: 'Medium Sand' },
    { value: 'sand_dense', label: 'Dense Sand' },
    { value: 'gravel', label: 'Gravel' },
    { value: 'moraine', label: 'Moraine' },
    { value: 'xps', label: 'XPS Insulation' },
    { value: 'bedrock', label: 'Bedrock' }
  ];


  // Valid typical ranges for soil parameters (from materials_and_soil.py)
  const soilParameterRanges = {
    gamma: {
      symbol: 'γ',
      unit: 'kN/m³',
      ranges: [
        { label: 'Soft Clay', range: '16 – 19' },
        { label: 'Stiff Clay', range: '17 – 21' },
        { label: 'Hard Clay', range: '18 – 22' },
        { label: 'Soft Silt', range: '17 – 20' },
        { label: 'Med Silt', range: '18 – 21' },
        { label: 'Loose Sand', range: '16 – 18' },
        { label: 'Med Sand', range: '17 – 19' },
        { label: 'Dense Sand', range: '18 – 20' },
        { label: 'Gravel', range: '19 – 21' },
        { label: 'Loose Gravel', range: '19 – 21' },
        { label: 'Dense Gravel', range: '20 – 22' },
        { label: 'Moraine', range: '20 – 22' },
        { label: 'XPS', range: '0.3 – 0.5' },
        { label: 'Soft Bedrock', range: '24 – 26' },
        { label: 'Hard Bedrock', range: '25 – 29' }
      ]
    },
    cu: {
      symbol: 'cᵤ',
      unit: 'kPa',
      ranges: [
        { label: 'Soft Clay', range: '10 – 25' },
        { label: 'Stiff Clay', range: '30 – 100' },
        { label: 'Hard Clay', range: '75 – 200' },
        { label: 'Soft Silt', range: '15 – 30' },
        { label: 'Med Silt', range: '25 – 60' },
        { label: 'Sands/Gravels', range: '0 (Cohesionless)' }
      ]
    },
    phi: {
      symbol: 'φ',
      unit: '°',
      ranges: [
        { label: 'Clays', range: '0 – 15 (Residual)' },
        { label: 'Soft Silt', range: '20 – 30' },
        { label: 'Med Silt', range: '25 – 32' },
        { label: 'Loose Sand', range: '25 – 32' },
        { label: 'Med Sand', range: '30 – 36' },
        { label: 'Dense Sand', range: '33 – 40' },
        { label: 'Gravel', range: '35 – 45' },
        { label: 'Loose Gravel', range: '30 – 36' },
        { label: 'Dense Gravel', range: '36 – 42' },
        { label: 'Moraine', range: '30 – 40' },
        { label: 'XPS', range: '0 (Elastic)' },
        { label: 'Soft Bedrock', range: '40 – 50' },
        { label: 'Hard Bedrock', range: '45 – 60' }
      ]
    },
    E_modulus: {
      symbol: 'E',
      unit: 'MPa',
      ranges: [
        { label: 'Soft Clay', range: '1 – 4' },
        { label: 'Stiff Clay', range: '5 – 15' },
        { label: 'Hard Clay', range: '10 – 25' },
        { label: 'Soft Silt', range: '3 – 8' },
        { label: 'Med Silt', range: '7 – 15' },
        { label: 'Loose Sand', range: '10 – 25' },
        { label: 'Med Sand', range: '20 – 50' },
        { label: 'Dense Sand', range: '40 – 80' },
        { label: 'Gravel', range: '50 – 150' },
        { label: 'Loose Gravel', range: '30 – 60' },
        { label: 'Dense Gravel', range: '60 – 120' },
        { label: 'Moraine', range: '20 – 80' },
        { label: 'XPS', range: '10 – 20' },
        { label: 'Soft Bedrock', range: '100 – 500' },
        { label: 'Hard Bedrock', range: '2000 – 50000' }
      ]
    }
  };

  const renderParameterTooltip = (paramKey) => {
    const data = soilParameterRanges[paramKey];
    if (!data) return '';

    return (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          Typical {data.symbol} ({data.unit}) Ranges:
        </Typography>
        <Grid container spacing={0.5}>
          {data.ranges.map((item, idx) => (
            <React.Fragment key={idx}>
              <Grid item xs={7}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{item.label}:</Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{item.range}</Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );
  };

  // Custom foundation preset (4 piles in square pattern)
  const customFoundationPreset = {
    code: 'CUSTOM-4PILE',
    foundation_type: 'custom',
    pile_type: 'RTB',
    pile_size: 300,
    ptl_class: 3,
    num_piles: 4,
    rebar: 'Custom reinforcement',
    concrete: 'C35/45',
    dimensions: { L: 2.0, W: 2.0, H: 0.8 },
    spacing: 1.0,
    layout: [
      [-0.5, -0.5],  // Bottom-left
      [0.5, -0.5],   // Bottom-right
      [0.5, 0.5],    // Top-right
      [-0.5, 0.5]    // Top-left
    ]
  };

  // Load standard foundations on component mount
  useEffect(() => {
    const loadStandardFoundations = async () => {
      try {
        console.log('Loading all standard foundations...');
        const response = await axios.get('/api/foundations/standard-foundations/');
        console.log('Initial load response:', response.data);

        // Filter foundations by the default foundation type ('column')
        const currentFoundationType = 'column';
        console.log('Filtering initial foundations by type:', currentFoundationType);
        const filteredFoundations = filterStandardFoundationsForUi(
          response.data.filter(f => f.foundation_type === currentFoundationType)
        );
        console.log('Filtered foundations count:', filteredFoundations.length);
        setStandardFoundations(filteredFoundations);

        // After loading foundations, check if there's a saved foundation to restore
        const saved = loadFormData();
        if (saved) {
          console.log('Restoring saved form data');

          // Restore foundation selection
          if (saved.selectedFoundation) {
            // Check if it's a custom foundation (has pileData or isCustom flag)
            if (saved.selectedFoundation.isCustom || saved.selectedFoundation.pileData || saved.selectedFoundation.code?.startsWith('custom-')) {
              // Restore custom foundation directly (don't look in standard list)
              console.log('Restoring custom foundation:', saved.selectedFoundation.code);
              setSelectedFoundation(saved.selectedFoundation);
            } else {
              // Standard foundation - find it in the list
              const savedFoundation = response.data.find(f => f.code === saved.selectedFoundation.code);
              if (savedFoundation) {
                console.log('Restoring saved foundation:', savedFoundation.code);
                // Merge saved pileData if it exists (for modified standard foundations)
                const foundationToRestore = saved.selectedFoundation.pileData 
                  ? { ...savedFoundation, pileData: saved.selectedFoundation.pileData }
                  : savedFoundation;
                setSelectedFoundation(foundationToRestore);
                // Initialize ref with saved pileData
                if (saved.selectedFoundation.pileData) {
                  latestPileDataRef.current = saved.selectedFoundation.pileData;
                }
              } else {
                console.log('Saved foundation not found in current options, ignoring');
              }
            }
          }

          // Restore all other form fields
          setFoundationType(saved.foundationType || 'column');
          setPileLength(saved.pileLength || 12.0);
          setIsCustomFoundation(saved.isCustomFoundation || false);
          setCustomFoundationName(saved.customFoundationName || '');
          setPtlClass(saved.ptlClass || (saved.selectedFoundation?.ptl_class || 2));
          if (saved.soilLayers) setSoilLayers(soilLayersFromSaved(saved).map(normalizeSoilLayerModulus));
          if (saved.loads) setLoads(saved.loads);
          if (saved.rebarArea) setRebarArea(saved.rebarArea);
        }
      } catch (err) {
        console.error('Failed to load standard foundations:', err);
        console.error('Error details:', err.response?.data || err.message);
        // Fallback: load comprehensive sample data for testing
        const sampleFoundationsAll = [
          // Column foundations (VPA) - Fixed layouts relative to foundation center
          {
            code: 'VPA-2RTB250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 2,
            rebar: 'PR1 5-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 0.7, H: 0.75 },
            spacing: 0.85,
            layout: [[0, -0.35], [0, 0.35]] // Within W=0.7m (±0.35m)
          },
          {
            code: 'VPA-3RTB250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 3,
            rebar: '3kpl PR1 5-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 1.7, H: 0.6 },
            spacing: 0.85,
            layout: [[0, 0.4], [-0.4, -0.4], [0.4, -0.4]] // Within 1.7m square (±0.85m)
          },
          {
            code: 'VPA-4RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 4,
            rebar: '4kpl PR2 6-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 1.7, H: 0.65 },
            spacing: 0.85,
            layout: [[-0.4, -0.4], [-0.4, 0.4], [0.4, -0.4], [0.4, 0.4]] // Square pattern
          },
          {
            code: 'VPA-5RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 5,
            rebar: '4kpl PR3 6-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.95, W: 1.95, H: 0.95 },
            spacing: 1.2,
            layout: [[-0.5, -0.5], [-0.5, 0.5], [0.5, -0.5], [0.5, 0.5], [0, 0]] // Cross pattern
          },
          {
            code: 'VPA-6RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 6,
            rebar: '2kpl PR4 8-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 2.7, W: 1.6, H: 0.95 },
            spacing: 0.85,
            layout: [[-0.6, -0.35], [-0.6, 0], [-0.6, 0.35], [0.6, -0.35], [0.6, 0], [0.6, 0.35]] // Within L=2.7m (±1.35m), W=1.6m (±0.8m)
          },
          {
            code: 'VPA-7RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 7,
            rebar: '5 kpl PR5 7-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 2.7, W: 2.7, H: 1.1 },
            spacing: 0.85,
            layout: [[-0.6, -0.6], [0, -0.6], [0.6, -0.6], [-0.6, 0], [0.6, 0], [-0.6, 0.6], [0.6, 0.6]] // Within 2.7m square (±1.35m)
          },
          {
            code: 'VPA-8RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 8,
            rebar: '6kpl PR5 7-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 2.7, W: 2.7, H: 1.1 },
            spacing: 0.85,
            layout: [[-0.8, -0.8], [-0.4, -0.8], [0.4, -0.8], [0.8, -0.8],
                    [-0.8, 0.8], [-0.4, 0.8], [0.4, 0.8], [0.8, 0.8]] // Perimeter pattern
          },
          {
            code: 'VPA-9RTB-250-2',
            foundation_type: 'column',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 9,
            rebar: '6 kpl PR5 7-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 2.7, W: 2.7, H: 1.1 },
            spacing: 0.85,
            layout: [[-0.6, -0.6], [-0.3, -0.6], [0, -0.6], [0.3, -0.6], [0.6, -0.6],
                    [-0.6, 0], [0, 0], [0.6, 0],
                    [-0.6, 0.6], [-0.3, 0.6], [0, 0.6], [0.3, 0.6], [0.6, 0.6]] // 3x3 grid
          },

          // Wall foundations (VSA) - Typically narrower in one dimension
          {
            code: 'VSA-2RTB250-2',
            foundation_type: 'wall',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 2,
            rebar: 'PR1 5-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 0.7, H: 0.75 },
            spacing: 0.85,
            layout: [[0, -0.3], [0, 0.3]] // Narrow wall foundation
          },
          {
            code: 'VSA-3RTB-250-2',
            foundation_type: 'wall',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 3,
            rebar: '3 kpl PR1 5-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 1.7, H: 0.6 },
            spacing: 0.85,
            layout: [[0, 0.4], [-0.4, -0.4], [0.4, -0.4]] // Within dimensions
          },
          {
            code: 'VSA-4RTB-250-2',
            foundation_type: 'wall',
            pile_type: 'RTB',
            pile_size: 250,
            ptl_class: 2,
            num_piles: 4,
            rebar: '2 kpl PR1 5-T20-k80',
            concrete: 'C35/45',
            dimensions: { L: 1.7, W: 1.7, H: 0.75 },
            spacing: 0.85,
            layout: [[-0.4, -0.4], [-0.4, 0.4], [0.4, -0.4], [0.4, 0.4]] // Square pattern within bounds
          }
        ];
        const sampleFoundations = filterStandardFoundationsForUi(sampleFoundationsAll);
        console.log('Using fallback sample data:', sampleFoundations);
        setStandardFoundations(sampleFoundations);

        // After setting fallback data, check if there's a saved foundation to restore
        const saved = loadFormData();
        if (saved) {
          console.log('Restoring saved form data from fallback');

          // Restore foundation selection
          if (saved.selectedFoundation) {
            // Check if it's a custom foundation
            if (saved.selectedFoundation.isCustom || saved.selectedFoundation.pileData || saved.selectedFoundation.code?.startsWith('custom-')) {
              // Restore custom foundation directly
              console.log('Restoring custom foundation from fallback:', saved.selectedFoundation.code);
              setSelectedFoundation(saved.selectedFoundation);
            } else {
              // Standard foundation - find it in the list
              const savedFoundation = sampleFoundationsAll.find(f => f.code === saved.selectedFoundation.code);
              if (savedFoundation) {
                console.log('Restoring saved foundation from fallback data:', savedFoundation.code);
                // Merge saved pileData if it exists
                const foundationToRestore = saved.selectedFoundation.pileData 
                  ? { ...savedFoundation, pileData: saved.selectedFoundation.pileData }
                  : savedFoundation;
                setSelectedFoundation(foundationToRestore);
              } else {
                console.log('Saved foundation not found in fallback data, ignoring');
              }
            }
          }

          // Restore all other form fields
          setFoundationType(saved.foundationType || 'column');
          setPileLength(saved.pileLength || 12.0);
          setIsCustomFoundation(saved.isCustomFoundation || false);
          setCustomFoundationName(saved.customFoundationName || '');
          setPtlClass(saved.ptlClass || (saved.selectedFoundation?.ptl_class || 2));
          if (saved.soilLayers) setSoilLayers(soilLayersFromSaved(saved).map(normalizeSoilLayerModulus));
          if (saved.loads) setLoads(saved.loads);
          if (saved.rebarArea) setRebarArea(saved.rebarArea);
        }
      }
    };
    loadStandardFoundations();
  }, []);

  // Load foundations by type
  const loadFoundationsByType = async (foundationType) => {
    try {
      console.log('Loading foundations for type:', foundationType);
      const response = await axios.get(`/api/foundations/standard-foundations/?foundation_type=${foundationType}`);
      console.log('API response:', response.data);
      const forType = (response.data || []).filter((f) => f.foundation_type === foundationType);
      setStandardFoundations(filterStandardFoundationsForUi(forType));

      // Check if current selectedFoundation is still valid for this type
      // Only clear if the foundation code doesn't exist in the new filtered list
      if (selectedFoundation && selectedFoundation.foundation_type !== foundationType) {
        const foundationStillExists = forType.some(f => f.code === selectedFoundation.code);
        if (!foundationStillExists) {
          console.log('Clearing selected foundation - not valid for new type (code not found)');
          setSelectedFoundation(null);
          setIsCustomFoundation(false);
        } else {
          console.log('Keeping selected foundation - exists in new type list');
        }
      }
    } catch (err) {
      console.error('Failed to load foundations by type:', err);
      console.error('Error details:', err.response?.data || err.message);
      // Fallback: filter local data if API fails
      const filteredFoundations = filterStandardFoundationsForUi(
        standardFoundations.filter(f => f.foundation_type === foundationType)
      );
      console.log('Filtered foundations for type:', foundationType, filteredFoundations);
      setStandardFoundations(filteredFoundations);

      // Check if current selectedFoundation is still valid for this type
      // Only clear if the foundation code doesn't exist in the new filtered list
      if (selectedFoundation && selectedFoundation.foundation_type !== foundationType) {
        const foundationStillExists = filteredFoundations.some(f => f.code === selectedFoundation.code);
        if (!foundationStillExists) {
          console.log('Clearing selected foundation - not valid for new type (fallback, code not found)');
          setSelectedFoundation(null);
          setIsCustomFoundation(false);
        } else {
          console.log('Keeping selected foundation - exists in fallback filtered list');
        }
      }
    }
  };

  // Update state when foundation is selected
  useEffect(() => {
    if (selectedFoundation) {
      console.log('selectedFoundation changed:', selectedFoundation.code);
      setProjectData(prev => ({
        ...prev,
        foundation_type: selectedFoundation.foundation_type
      }));
      setIsCustomFoundation(selectedFoundation.foundation_type === 'custom' || selectedFoundation.isCustom);
    } else {
      console.log('selectedFoundation is null');
    }
  }, [selectedFoundation]);

  useEffect(() => {
    if (foundationType === 'ground_facing_slab' && !selectedFoundation) {
      setSelectedFoundation(buildSlabFoundationWithLayout(DEFAULT_GROUND_SLAB_FOUNDATION, slabOptions));
    }
  }, [foundationType, selectedFoundation, slabOptions]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    // Ensure pileData is included in selectedFoundation when saving
    let foundationToSave = selectedFoundation;
    if (latestPileDataRef.current && latestPileDataRef.current.length > 0 && selectedFoundation) {
      foundationToSave = {
        ...selectedFoundation,
        pileData: latestPileDataRef.current,
        layout: latestPileDataRef.current.map(pile => [pile.x, pile.y, pile.length || pileLength])
      };
    }
    
    const formData = {
      projectData,
      foundationType,
      selectedFoundation: foundationToSave,
      pileLength,
      isCustomFoundation,
      customFoundationName,
      ptlClass,
      soilProfileVersion: SOIL_PROFILE_VERSION,
      soilLayers,
      loads,
      rebarArea,
      slabOptions,
    };
    saveFormData(formData);
  }, [projectData, foundationType, selectedFoundation, pileLength, isCustomFoundation, customFoundationName, ptlClass, soilLayers, loads, rebarArea, slabOptions]);

  // Custom foundations storage
  const CUSTOM_FOUNDATIONS_KEY = 'custom_foundations';
  
  // Generate default custom foundation name: "custom-VPA/VSA-number_of_piles-PTL_class"
  const generateCustomFoundationName = (foundation) => {
    const foundationPrefix = foundationType === 'wall' ? 'VSA' : 'VPA';
    const numPiles = foundation?.num_piles || foundation?.layout?.length || foundation?.pileData?.length || 0;
    const ptl = ptlClass || foundation?.ptl_class || 2;
    return `custom-${foundationPrefix}-${numPiles}-${ptl}`;
  };
  
  const saveCustomFoundation = (foundation) => {
    try {
      const customFoundations = JSON.parse(localStorage.getItem(CUSTOM_FOUNDATIONS_KEY) || '[]');
      // Use custom name if provided, otherwise generate default name
      const foundationName = foundation.customName || customFoundationName || generateCustomFoundationName(foundation);
      const customId = foundation.customCode || foundationName; // Use customCode if set, otherwise use name
      
      const customFoundation = {
        ...foundation,
        code: customId,
        customCode: customId, // Store custom code separately
        customName: foundationName, // Store display name
        foundation_type: foundation.foundation_type || foundationType,
        ptl_class: ptlClass || foundation.ptl_class || 2,
        isCustom: true
      };
      
      // Check if foundation with same code exists, update it; otherwise add new
      const existingIndex = customFoundations.findIndex(f => f.code === customId || f.customCode === customId);
      if (existingIndex >= 0) {
        customFoundations[existingIndex] = customFoundation;
      } else {
        customFoundations.push(customFoundation);
      }
      
      localStorage.setItem(CUSTOM_FOUNDATIONS_KEY, JSON.stringify(customFoundations));
      return customFoundation;
    } catch (e) {
      console.warn('Failed to save custom foundation:', e);
      return foundation;
    }
  };
  
  const loadCustomFoundations = () => {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_FOUNDATIONS_KEY) || '[]');
    } catch (e) {
      console.warn('Failed to load custom foundations:', e);
      return [];
    }
  };

  // Function to mark foundation as custom when modified
  const markAsCustom = () => {
    if (!isCustomFoundation) {
      setIsCustomFoundation(true);
      setFoundationType('custom'); // Update foundation type dropdown to 'custom'
      setProjectData(prev => ({
        ...prev,
        foundation_type: 'custom'
      }));
      
      // Generate default custom foundation name if not already set
      if (selectedFoundation && !customFoundationName) {
        const defaultName = generateCustomFoundationName(selectedFoundation);
        setCustomFoundationName(defaultName);
      }
    }
    // Save current foundation as custom
    if (selectedFoundation) {
      const customFoundation = saveCustomFoundation(selectedFoundation);
      setSelectedFoundation(customFoundation);
    }
  };

  // Input validation with specific error messages
  const validateInputs = () => {
    const errors = [];

    if (!projectData.name.trim()) {
      errors.push("Project name is required");
    }

    if (!selectedFoundation) {
      errors.push("Please select a foundation from the dropdown");
    }

    if (foundationType === 'ground_facing_slab') {
      const dims = selectedFoundation?.dimensions;
      if (!dims?.L || !dims?.W || !dims?.H) {
        errors.push('Ground-facing slab requires length, width, and thickness');
      }
      if ((slabOptions.loads?.concentrated?.force_kn ?? slabOptions.point_load_kn) < 0) {
        errors.push('Concentrated load must be non-negative');
      }
      if (slabOptions.pile_grid?.enabled && (pileLength < 3 || pileLength > 50)) {
        errors.push('Pile length must be between 3m and 50m when piles are enabled');
      }
    } else if (foundationType !== 'spread_footing') {
      if (pileLength < 3 || pileLength > 50) {
        errors.push("Pile length must be between 3m and 50m");
      }
    }

    if (soilLayers.length === 0) {
      errors.push("At least one soil layer is required");
    }

    soilLayers.forEach((layer, index) => {
      if (layer.bottom_depth <= layer.top_depth) {
        errors.push(`Soil layer ${index + 1}: Bottom depth must be greater than top depth`);
      }
      if (layer.unit_weight <= 0) {
        errors.push(`Soil layer ${index + 1}: Unit weight must be positive`);
      }
      if (layer.modulus <= 0) {
        errors.push(`Soil layer ${index + 1}: Soil modulus must be positive`);
      }
    });

    if (Math.abs(loads.vertical_load) > 10000) {
      errors.push("Vertical load seems unreasonably high (>10,000 kN)");
    }

    return errors;
  };

  const handleFoundationSelection = (selectedValue) => {
    console.log('=== handleFoundationSelection START ===');
    console.log('selectedValue:', selectedValue);
    console.log('selectedValue type:', typeof selectedValue);
    console.log('selectedValue length:', selectedValue?.length);

    if (!selectedValue) {
      console.warn('No value selected');
      return;
    }

    if (selectedValue === 'CUSTOM-4PILE') {
      console.log('Setting custom foundation preset');
      setSelectedFoundation(customFoundationPreset);
      setIsCustomFoundation(true);
      return;
    }

    try {
      // Try to find in standard foundations first
      let foundation = standardFoundations.find(f => f.code === selectedValue);
      
      // If not found, try custom foundations
      if (!foundation) {
        console.log('Not found in standard, checking custom foundations');
        const customFoundations = loadCustomFoundations();
        foundation = customFoundations.find(f => f.code === selectedValue);
      }
      
      if (foundation) {
        console.log('Found foundation:', foundation.code);
        
        // Extract PTL class from foundation code (last character) or use from foundation
        const ptlClassVal = foundation.ptl_class || parseInt(foundation.code.slice(-1)) || 2;
        
        // Ensure all required fields are present
        const updatedFoundation = {
          ...foundation,
          ptl_class: ptlClassVal,
          code: foundation.code || selectedValue,
          foundation_type: foundation.foundation_type || foundationType,
          pile_type: foundation.pile_type || 'RTB',
          pile_size: foundation.pile_size || 250,
          num_piles: foundation.num_piles || (foundation.layout ? foundation.layout.length : 0),
          layout: foundation.layout || [],
          dimensions: foundation.dimensions || { L: 2.0, W: 2.0, H: 1.0 },
          spacing: foundation.spacing || 1.0,
          concrete: foundation.concrete || 'C35/45',
          rebar: foundation.rebar || ''
        };
        
        console.log('Setting selectedFoundation:', updatedFoundation.code);
        console.log('updatedFoundation object:', updatedFoundation);
        setSelectedFoundation(updatedFoundation);
        console.log('setSelectedFoundation called, new state should be:', updatedFoundation.code);
        setPtlClass(ptlClassVal);
        setIsCustomFoundation(foundation.isCustom || false);
        
        if (foundation.customName) {
          setCustomFoundationName(foundation.customName);
        } else {
          setCustomFoundationName('');
        }
        
        // Update foundationType AFTER setting foundation to avoid clearing it
        if (foundation.foundation_type && foundation.foundation_type !== foundationType) {
          console.log('Updating foundationType programmatically to:', foundation.foundation_type);
          setIsProgrammaticTypeChange(true);
          setFoundationType(foundation.foundation_type);
        }
        
        console.log('=== handleFoundationSelection COMPLETE ===');
      } else {
        console.error('Foundation not found:', selectedValue);
      }
    } catch (error) {
      console.error('Error in handleFoundationSelection:', error);
    }
  };

  const handleRunAnalysis = async () => {
    console.log('=== HANDLE RUN ANALYSIS CALLED ===');
    console.log('Starting analysis...');
    setLoading(true);
    setError(null);
    setResults(null);

    // Validate inputs first
    const validationErrors = validateInputs();
    console.log('Validation errors:', validationErrors);
    console.log('Validation errors length:', validationErrors.length);

    if (validationErrors.length > 0) {
      console.log('VALIDATION FAILED - not proceeding with API calls');
      setError(validationErrors.join('\n• '));
      setLoading(false);
      return;
    }

    console.log('VALIDATION PASSED - proceeding with API calls...');

    try {
      console.log('Creating project...');
      console.log('Project data:', projectData);
      console.log('Selected foundation:', selectedFoundation);
      console.log('Soil layers:', soilLayers);

      // Create project first
      console.log('Creating project with data:', projectData);
      console.log('Project API URL:', '/api/foundations/projects/');
      let projectResponse;
      try {
        projectResponse = await axios.post('/api/foundations/projects/', {
          ...projectData,
          foundation_type: foundationType,
        });
        console.log('Project creation response received:', projectResponse);
        console.log('Project response data:', projectResponse.data);
      } catch (projectError) {
        console.error('Project creation failed:', projectError);
        console.error('Project error details:', projectError.response?.data || projectError.message);
        throw projectError; // Re-throw so it gets caught by outer catch
      }

      if (!projectResponse.data || !projectResponse.data.id) {
        throw new Error('Project creation failed - no project ID returned');
      }

      const projectId = projectResponse.data.id; // Django returns 'id', not 'project_id'
      console.log('Created project with ID:', projectId);

      // Safety check for selectedFoundation
      console.log('Checking selectedFoundation before analysis:', selectedFoundation);
      console.log('selectedFoundation.code:', selectedFoundation?.code);
      if (!selectedFoundation || (!selectedFoundation.code && foundationType !== 'ground_facing_slab')) {
        console.error('Foundation selection is invalid:', selectedFoundation);
        setError('Foundation selection is invalid. Please select a foundation first.');
        setLoading(false);
        return;
      }
      
      console.log('Using foundation for analysis:', selectedFoundation.code);

      // If foundation is custom or has been modified, ensure it's saved before analysis
      let foundationToUse = selectedFoundation;
      if (foundationType === 'ground_facing_slab') {
        const gridOpts = {
          ...slabOptions,
          pile_grid: {
            ...slabOptions.pile_grid,
            pile_length_m: pileLength,
          },
        };
        foundationToUse = buildSlabFoundationWithLayout(selectedFoundation, gridOpts);
      } else if (isCustomFoundation || selectedFoundation.isCustom || selectedFoundation.pileData) {
        // Update foundation with current PTL class and custom name before saving
        // IMPORTANT: Ensure we preserve pileData and layout from selectedFoundation
        const foundationWithUpdates = {
          ...selectedFoundation,
          ptl_class: ptlClass || selectedFoundation.ptl_class || 2,
          customName: customFoundationName || generateCustomFoundationName(selectedFoundation),
          customCode: customFoundationName || generateCustomFoundationName(selectedFoundation),
          // Explicitly preserve pileData and layout to ensure they're not lost
          pileData: selectedFoundation.pileData || foundationToUse.pileData,
          layout: selectedFoundation.layout || foundationToUse.layout,
          pile_layout: selectedFoundation.pile_layout || foundationToUse.pile_layout
        };
        // Save/update custom foundation before analysis
        foundationToUse = saveCustomFoundation(foundationWithUpdates);
        setSelectedFoundation(foundationToUse);
        console.log('Saved custom foundation before analysis:', foundationToUse);
        console.log('foundationToUse.pileData:', foundationToUse.pileData);
        console.log('foundationToUse.layout:', foundationToUse.layout);
      }

      // Extract layout - prioritize latest pileData from ref (most up-to-date), then selectedFoundation, then foundationToUse
      let finalLayout = [];
      // First check ref (most up-to-date, synchronous access)
      if (latestPileDataRef.current && latestPileDataRef.current.length > 0) {
        // Use pileData from ref (most up-to-date, synchronous)
        finalLayout = latestPileDataRef.current.map(pile => [
          pile.x || 0,
          pile.y || 0,
          pile.angle_x || 0,
          pile.angle_y || 0,
          pile.length || pileLength
        ]);
        console.log('Using latestPileDataRef.current for layout:', finalLayout);
      } else if (selectedFoundation.pileData && selectedFoundation.pileData.length > 0) {
        // Use pileData from selectedFoundation state
        finalLayout = selectedFoundation.pileData.map(pile => [
          pile.x || 0,
          pile.y || 0,
          pile.angle_x || 0,
          pile.angle_y || 0,
          pile.length || pileLength
        ]);
        console.log('Using selectedFoundation.pileData for layout:', finalLayout);
      } else if (foundationToUse.pileData && foundationToUse.pileData.length > 0) {
        // Fallback to foundationToUse.pileData
        finalLayout = foundationToUse.pileData.map(pile => [
          pile.x || 0,
          pile.y || 0,
          pile.angle_x || 0,
          pile.angle_y || 0,
          pile.length || pileLength
        ]);
        console.log('Using foundationToUse.pileData for layout:', finalLayout);
      } else if (selectedFoundation.layout && selectedFoundation.layout.length > 0) {
        // Use layout from selectedFoundation (may already include lengths)
        finalLayout = selectedFoundation.layout.map(p => {
          if (Array.isArray(p) && p.length >= 2) {
            // Ensure 5-parameter format: [x, y, angle_x, angle_y, deltaZ]
            if (p.length === 2) return [p[0], p[1], 0, 0, pileLength];
            if (p.length === 3) return [p[0], p[1], 0, 0, p[2]];
            if (p.length === 4) return [p[0], p[1], p[2], 0, p[3]]; // Should rarely happen
            return p;
          }
          return p;
        });
        console.log('Using selectedFoundation.layout:', finalLayout);
      } else if (foundationToUse.layout && foundationToUse.layout.length > 0) {
        // Use layout array (may already include lengths)
        finalLayout = foundationToUse.layout.map(p => {
          if (Array.isArray(p) && p.length >= 2) {
            // Ensure 5-parameter format: [x, y, angle_x, angle_y, deltaZ]
            if (p.length === 2) return [p[0], p[1], 0, 0, pileLength];
            if (p.length === 3) return [p[0], p[1], 0, 0, p[2]];
            return p;
          }
          return p;
        });
        console.log('Using layout array:', finalLayout);
      } else if (foundationToUse.pile_layout && foundationToUse.pile_layout.length > 0) {
        // Fallback to pile_layout (add lengths)
        finalLayout = foundationToUse.pile_layout.map(p => {
          if (Array.isArray(p) && p.length >= 2) {
            // Ensure 5-parameter format
            if (p.length === 2) return [p[0], p[1], 0, 0, pileLength];
            if (p.length === 3) return [p[0], p[1], 0, 0, p[2]];
            return p;
          }
          return p;
        });
        console.log('Using pile_layout:', finalLayout);
      } else {
        // No layout found - this is an error for custom foundations
        console.error('ERROR: No layout found for foundation:', foundationToUse);
        console.error('foundationToUse keys:', Object.keys(foundationToUse));
        console.error('foundationToUse.pileData:', foundationToUse.pileData);
        console.error('foundationToUse.layout:', foundationToUse.layout);
        console.error('foundationToUse.pile_layout:', foundationToUse.pile_layout);
      }
      
      // Ensure layout is not empty for custom foundations
      if (
        foundationType !== 'ground_facing_slab'
        && (foundationToUse.isCustom || foundationToUse.code?.startsWith('custom-'))
        && finalLayout.length === 0
      ) {
        console.error('ERROR: Custom foundation has no layout!');
        setError('Custom foundation has no pile layout. Please edit the foundation layout table.');
        setLoading(false);
        return;
      }

      // Extract per-pile lengths from layout if available
      // Pull pile lengths from the 5th slot in each layout row (x, y, ax, ay, length)
      const pile_lengths = foundationToUse.pile_lengths || 
        (finalLayout.length > 0
          ? finalLayout.map(p => {
              if (!Array.isArray(p)) return pileLength;
              if (p.length >= 5 && typeof p[4] === 'number') return p[4];
              if (p.length >= 3 && typeof p[p.length - 1] === 'number') return p[p.length - 1];
              return pileLength;
            })
          : [pileLength]);
      
      // Calculate average pile length
      const avgPileLength = pile_lengths.length > 0 
        ? pile_lengths.reduce((a, b) => a + b, 0) / pile_lengths.length 
        : pileLength;
      
      const isSpread = foundationToUse.foundation_type === 'spread_footing';
      const isGroundSlab = foundationToUse.foundation_type === 'ground_facing_slab';
      const checkPunch = !isSpread && !isGroundSlab && Boolean(projectData.check_column_punching);
      const soilProfile = isGroundSlab
        ? applySupportLayersToProfile(soilLayers, slabOptions)
        : (soilLayers || []);

      const analysisInput = {
        project_id: projectId,
        pile_code: foundationToUse.code || 'GROUND_FACING_SLAB',
        pile_length: isGroundSlab ? pileLength : avgPileLength,
        pile_lengths: isGroundSlab && finalLayout.length === 0 ? [] : pile_lengths,
        concrete_grade: foundationToUse.concrete || 'C35/45',
        soil_layers: soilProfile.map((layer) => {
          const cleaned = { ...layer };
          ['N60', 'N_SPT', 'spt_n60', 'fines_content_pct'].forEach((key) => {
            if (cleaned[key] === '' || cleaned[key] == null) delete cleaned[key];
          });
          return cleaned;
        }),
        vertical_load: loads.vertical_load || 0,
        horizontal_load_y: loads.horizontal_load_y || 0,
        horizontal_load_x: loads.horizontal_load_x || 0,
        moment_y: loads.moment_y || 0,
        moment_x: loads.moment_x || 0,
        moment_z: loads.moment_z || 0,
        seismic_enabled: projectData.seismic_enabled,
        ...(projectData.seismic_enabled
          ? { seismic_params: projectData.seismic_params || {} }
          : {}),
        tension_resistance_enabled: projectData.tension_resistance_enabled,
        embedment_depth: projectData.embedment_depth || 0,
        pile_head_fixity: projectData.pile_head_fixity !== undefined ? projectData.pile_head_fixity : 0.0,
        pile_head_fixity_mode: projectData.pile_head_fixity_mode || 'preset',
        pile_head_fixity_preset: projectData.pile_head_fixity_preset || 'pinned',
        pile_head_fixity_computed: projectData.pile_head_fixity_computed || {
          cap_embedment_m: 0,
          top_rebar_development: 'none',
          cap_thickness_m: 0,
        },
        passive_mobilisation: projectData.passive_mobilisation || 'hyperbolic',
        allow_piled_soil_bearing: Boolean(projectData.allow_piled_soil_bearing) && !soilLayers.some((layer) => layer.is_settling),
        passive_ref_ratio_h: parseFlexibleFloat(projectData.passive_ref_ratio_h) || 0.02,
        ptl_class: ptlClass || foundationToUse.ptl_class || 2,
        foundation_type: foundationToUse.foundation_type || 'column',
        layout: finalLayout, // Include layout with lengths (required for custom foundations)
        pile_type: foundationToUse.pile_type || 'RTB',
        pile_size: foundationToUse.pile_size || 250,
        dimensions: foundationToUse.dimensions || { L: 2.0, W: 2.0, H: 1.0 },
        spacing: foundationToUse.spacing || 1.0,
        rebar: foundationToUse.rebar || '', // Include rebar info
        check_column_punching: checkPunch,
        ...(checkPunch
          ? {
              column_side_x_m: parseFlexibleFloat(projectData.column_side_x_m),
              column_side_y_m: parseFlexibleFloat(projectData.column_side_y_m),
            }
          : {}),
        ...(isGroundSlab
          ? {
              slab_options: {
                ...slabOptions,
                // Gravel and XPS are already the top soil-profile layers.
                gravel: { ...slabOptions.gravel, enabled: false },
                xps: { ...slabOptions.xps, enabled: false },
                loads: slabLoadsForAnalysis(slabOptions.loads, { advanced: showAdvanced }),
                pile_grid: { ...slabOptions.pile_grid, pile_length_m: pileLength },
              },
              mesh: meshNodeCounts(
                foundationToUse.dimensions?.L || 6,
                foundationToUse.dimensions?.W || 6,
                slabOptions.mesh?.spacing_m ?? 1,
              ),
              cover_m: slabOptions.cover_m,
              design_basis: {
                gamma_g: slabOptions.design_basis?.gamma_g ?? 1.35,
                gamma_q: slabOptions.design_basis?.gamma_q ?? 1.5,
              },
            }
          : {}),
      };

      const gwd = parseFlexibleFloat(projectData.groundwater_depth);
      if (gwd > 0) {
        analysisInput.groundwater_depth = gwd;
      }
      const prd = parseFlexibleFloat(projectData.passive_ref_displacement_m);
      if (prd > 0) {
        analysisInput.passive_ref_displacement_m = prd;
      }

      console.log('=== ANALYSIS INPUT DEBUG ===');
      console.log('selectedFoundation.pileData:', selectedFoundation.pileData);
      console.log('selectedFoundation.layout:', selectedFoundation.layout);
      console.log('foundationToUse.pileData:', foundationToUse.pileData);
      console.log('foundationToUse.layout:', foundationToUse.layout);
      console.log('finalLayout:', finalLayout);
      console.log('finalLayout length:', finalLayout.length);
      console.log('Analysis input being sent:', {
        ...analysisInput,
        layout_length: finalLayout.length,
        layout_preview: finalLayout.slice(0, 3), // First 3 piles
        soil_layers_count: (soilLayers || []).length
      });

      console.log('Analysis input:', analysisInput);

      // Run analysis
      console.log('Calling analysis API...');
      console.log('API URL:', `/api/foundations/projects/${projectId}/analyze/`);
      console.log('Project ID:', projectId);
      let analysisResponse;
      try {
        analysisResponse = await axios.post(`/api/foundations/projects/${projectId}/analyze/`, analysisInput);
        console.log('Analysis response received:', analysisResponse);
        console.log('Response data:', analysisResponse.data);
      } catch (apiError) {
        console.error('API call failed:', apiError);
        console.error('Error details:', apiError.response?.data || apiError.message);
        throw apiError; // Re-throw so it gets caught by outer catch
      }

      setResults(analysisResponse.data);

      if (onAnalysisComplete) {
        onAnalysisComplete(analysisResponse.data);
      }

      historyRef.current?.saveAuto?.({ results_data: analysisResponse.data, status: 'completed' });

    } catch (err) {
      console.error('Analysis error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.error ||
                          err.response?.data?.message ||
                          err.message ||
                          'Analysis failed. Please check your inputs.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const makeSoilLayer = (topDepth, thickness) => ({
    soil_type: 'clay_stiff',
    top_depth: topDepth,
    bottom_depth: topDepth + thickness,
    cohesion: 50,
    friction_angle: 0,
    unit_weight: 19.0,
    E_modulus: 8.0,
    modulus: 8.0,
    is_settling: false,
    N60: '',
    fines_content_pct: '',
    is_saturated: false,
  });

  const addSoilLayer = () => {
    if (soilLayers.length === 0) {
      setSoilLayers([makeSoilLayer(0, 5)]);
      return;
    }
    const lastLayer = soilLayers[soilLayers.length - 1];
    setSoilLayers([...soilLayers, makeSoilLayer(Number(lastLayer.bottom_depth), 5)]);
  };

  const insertSoilLayer = (afterIndex) => {
    const upper = soilLayers[afterIndex];
    if (!upper) return;
    const thickness = 2;
    const inserted = makeSoilLayer(Number(upper.bottom_depth), thickness);
    const next = [...soilLayers];
    next.splice(afterIndex + 1, 0, inserted);
    for (let i = afterIndex + 2; i < next.length; i += 1) {
      next[i] = {
        ...next[i],
        top_depth: Number(next[i].top_depth) + thickness,
        bottom_depth: Number(next[i].bottom_depth) + thickness,
      };
    }
    setSoilLayers(next);
  };

  const updateSoilLayer = (index, field, value) => {
    const updatedLayers = [...soilLayers];
    
    // Numeric fields that should be parsed immediately to avoid string pollution in JSON
    const numericFields = [
      'top_depth', 'bottom_depth', 'unit_weight', 'cohesion', 
      'friction_angle', 'E_modulus', 'modulus', 'alpha_factor', 'beta_factor',
    ];

    if (field === 'N60' || field === 'fines_content_pct') {
      updatedLayers[index] = {
        ...updatedLayers[index],
        [field]: value === '' || value === null || value === undefined ? '' : parseFlexibleFloat(value),
      };
    } else if (numericFields.includes(field)) {
      updatedLayers[index] = { ...updatedLayers[index], [field]: parseFlexibleFloat(value) };
    } else if (field === 'is_settling' || field === 'is_saturated') {
      updatedLayers[index] = { ...updatedLayers[index], [field]: Boolean(value) };
    } else {
      updatedLayers[index] = { ...updatedLayers[index], [field]: value };
    }

    // Ensure layer continuity when depths change
    if (field === 'top_depth' || field === 'bottom_depth') {
      const numValue = updatedLayers[index][field]; // Use the already parsed value
      if (numValue !== undefined) {
        if (field === 'top_depth' && index > 0) {
          // Ensure top_depth matches previous layer's bottom_depth
          const prevBottom = updatedLayers[index - 1].bottom_depth;
          if (numValue !== prevBottom) {
            updatedLayers[index].top_depth = prevBottom;
          }
        } else if (field === 'top_depth' && index === 0) {
          // First layer must start at 0
          updatedLayers[index].top_depth = 0;
        } else if (field === 'bottom_depth') {
          // Update next layer's top_depth if it exists
          if (index < updatedLayers.length - 1) {
            updatedLayers[index + 1].top_depth = updatedLayers[index].bottom_depth;
          }
        }
      }
    }

    // Auto-populate default values when soil type changes
    // Values MUST match backend SOIL_DATABASE in materials_and_soil.py exactly
    if (field === 'soil_type') {
      // Map frontend soil types to backend SOIL_DATABASE defaults
      // These values come from structural_app/backend/Paalulaattaprojekti/materials_and_soil.py
      const soilDefaults = {
        clay_soft: { cohesion: 15, friction_angle: 0, unit_weight: 18.0, E_modulus: 2.0 },
        clay_stiff: { cohesion: 50, friction_angle: 0, unit_weight: 19.0, E_modulus: 8.0 },
        clay_hard: { cohesion: 100, friction_angle: 0, unit_weight: 20.0, E_modulus: 15.0 },
        sand_loose: { cohesion: 0, friction_angle: 28, unit_weight: 17.0, E_modulus: 15.0 },
        sand_medium: { cohesion: 0, friction_angle: 32, unit_weight: 18.0, E_modulus: 30.0 },
        sand_dense: { cohesion: 0, friction_angle: 35, unit_weight: 19.0, E_modulus: 50.0 },
        gravel: { cohesion: 0, friction_angle: 38, unit_weight: 20.0, E_modulus: 100.0 },
        silt: { cohesion: 0, friction_angle: 28, unit_weight: 19.0, E_modulus: 10.0 },
        moraine: { cohesion: 0, friction_angle: 38, unit_weight: 20.5, E_modulus: 80.0 },
        xps: { cohesion: 0, friction_angle: 0, unit_weight: 0.4, E_modulus: 10.0 },
        bedrock: { cohesion: 0, friction_angle: 50, unit_weight: 27.0, E_modulus: 5000.0 }
      };
      
      if (soilDefaults[value]) {
        const defaults = soilDefaults[value];
        updatedLayers[index] = {
          ...updatedLayers[index],
          cohesion: defaults.cohesion,
          friction_angle: defaults.friction_angle,
          unit_weight: defaults.unit_weight,
          E_modulus: defaults.E_modulus
        };
      }
    }

    setSoilLayers(updatedLayers);
  };

  const removeSoilLayer = (index) => {
    if (soilLayers.length <= 1) return;
    let cursor = 0;
    const next = soilLayers
      .filter((_, i) => i !== index)
      .map((layer) => {
        const thick = Math.max(0.001, Number(layer.bottom_depth) - Number(layer.top_depth));
        const updated = {
          ...layer,
          top_depth: cursor,
          bottom_depth: cursor + thick,
        };
        cursor = updated.bottom_depth;
        return updated;
      });
    setSoilLayers(next);
  };

  const setSeismicParam = (key, value) => {
    setProjectData((prev) => ({
      ...prev,
      seismic_params: { ...(prev.seismic_params || {}), [key]: value },
    }));
  };

  const handleExportCSV = async () => {
    if (!results) return;

    try {
      // For now, create a simple CSV from the results
      const csvData = [
        ['Field', 'Value'],
        ['Analysis Date', new Date().toISOString()],
        ['Foundation Type', foundationType],
        ['Compression Capacity', results.capacities?.compression || 0],
        ['Tension Capacity', results.capacities?.tension || 0],
        ['Lateral Capacity Y', results.capacities?.lateral_y || 0],
        ['Lateral Capacity X', results.capacities?.lateral_x || 0],
        ['Compression Utilization', results.utilization?.compression || 0],
        ['Tension Utilization', results.utilization?.tension || 0],
        ['Geotechnical Stiffness', results.stiffnesses?.k_geo || 0],
        ['Pile Vertical Stiffness', results.stiffnesses?.k_pile_vertical || 0],
        ['Foundation Cap Stiffness', results.stiffnesses?.k_cap || 0]
      ];

      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `foundation_analysis_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Engineering />
          Foundation Analysis Tool
        </Box>
        <CalculationHistory
          ref={historyRef}
          apiEndpoint="/api/foundations/saved-analyses"
          getSavePayload={getFoundationSavePayload}
          appLabel="Foundation analysis"
          onLoad={({ input_data, results_data }) => {
            if (input_data) {
              setProjectData((prev) => ({ ...prev, ...(input_data.projectData || {}) }));
              setFoundationType(input_data.foundationType || 'column');
              setSelectedFoundation(input_data.selectedFoundation || null);
              setPileLength(input_data.pileLength ?? 12.0);
              setSoilLayers(input_data.soilLayers || soilLayers);
              setLoads(input_data.loads || loads);
              setRebarArea(input_data.rebarArea ?? 0);
              if (input_data.slabOptions) setSlabOptions(input_data.slabOptions);
            }
            if (results_data) {
              setResults(results_data);
              onAnalysisComplete?.(results_data);
            }
          }}
          renderSummary={(item) => (
            <Typography variant="caption" color="text.secondary">
              {item.foundation_type} {item.pile_type ? `· ${item.pile_type}` : ''}
            </Typography>
          )}
        />
      </Typography>

      <Grid container spacing={3}>

        {/* Foundation Selection */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Foundation Selection" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Choose the foundation type first. The form then shows a short preset for that type. Open advanced inputs for the remaining fields.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Foundation Type</InputLabel>
                    <Select
                      value={foundationType}
                      onChange={(e) => {
                        // Skip if this is a programmatic change to avoid clearing selections
                        if (isProgrammaticTypeChange) {
                          setIsProgrammaticTypeChange(false);
                          return;
                        }

                        const newType = e.target.value;
                        console.log('Foundation type changed manually to:', newType);
                        setFoundationType(newType);

                        if (newType === 'spread_footing') {
                          setLoads((prev) => ({
                            ...prev,
                            horizontal_load_y: 0,
                            horizontal_load_x: 0,
                            moment_y: 0,
                            moment_x: 0,
                            moment_z: 0,
                          }));
                          // For spread footing, create a default footing configuration
                          const defaultFooting = {
                            code: 'SPREAD_FOOTING',
                            name: 'Spread Footing',
                            foundation_type: 'spread_footing',
                            dimensions: { L: 2.0, W: 2.0, H: 0.5 },
                            concrete: 'C25/30',
                            rebar: 'Standard footing reinforcement'
                          };
                          setSelectedFoundation(defaultFooting);
                          setIsCustomFoundation(false);
                        } else if (newType === 'ground_facing_slab') {
                          setSelectedFoundation(buildSlabFoundationWithLayout(DEFAULT_GROUND_SLAB_FOUNDATION, slabOptions));
                          setIsCustomFoundation(false);
                          setProjectData((prev) => ({ ...prev, foundation_type: 'ground_facing_slab' }));
                        } else {
                          setSelectedFoundation(null);
                          setIsCustomFoundation(false);
                          // Load foundations of the selected type
                          loadFoundationsByType(newType);
                        }
                      }}
                    >
                      <MenuItem value="column">Column (VPA)</MenuItem>
                      <MenuItem value="wall">Wall (VSA)</MenuItem>
                      <MenuItem value="spread_footing">Spread Footing</MenuItem>
                      <MenuItem value="ground_facing_slab">Ground-facing Slab</MenuItem>
                      <MenuItem value="custom">Custom Foundation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {foundationType !== 'spread_footing' && foundationType !== 'ground_facing_slab' && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Pile Length (m)"
                      value={pileLength}
                      onChange={(e) => {
                        setPileLength(parseFlexibleFloat(e.target.value));
                        markAsCustom();
                      }}
                      inputProps={{ min: 3, max: 50, step: 0.1 }}
                      disabled={!selectedFoundation}
                    />
                  </Grid>
                )}
                {foundationType === 'ground_facing_slab' && slabOptions.pile_grid?.enabled && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Pile Length (m)"
                      value={pileLength}
                      onChange={(e) => setPileLength(parseFlexibleFloat(e.target.value))}
                      inputProps={{ min: 3, max: 50, step: 0.1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label={foundationType === 'spread_footing' || foundationType === 'ground_facing_slab' ? 'Slab Length (m)' : 'Slab Length (m)'}
                    value={selectedFoundation?.dimensions?.L || ''}
                    onChange={(e) => {
                      if (selectedFoundation) {
                        const updatedFoundation = {
                          ...selectedFoundation,
                          dimensions: {
                            ...selectedFoundation.dimensions,
                            L: parseFlexibleFloat(e.target.value) || selectedFoundation.dimensions.L
                          }
                        };
                        if (foundationType === 'ground_facing_slab') {
                          setSelectedFoundation(buildSlabFoundationWithLayout(updatedFoundation, slabOptions));
                        } else {
                          setSelectedFoundation(updatedFoundation);
                          if (foundationType !== 'spread_footing') markAsCustom();
                        }
                      }
                    }}
                    inputProps={{ min: 0.5, max: 30, step: 0.1 }}
                    disabled={!selectedFoundation && foundationType !== 'ground_facing_slab'}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label={foundationType === 'spread_footing' ? "Footing Width (m)" : "Slab Width (m)"}
                    value={selectedFoundation?.dimensions?.W || ''}
                    onChange={(e) => {
                      if (selectedFoundation) {
                        const updatedFoundation = {
                          ...selectedFoundation,
                          dimensions: {
                            ...selectedFoundation.dimensions,
                            W: parseFlexibleFloat(e.target.value) || selectedFoundation.dimensions.W
                          }
                        };
                        if (foundationType === 'ground_facing_slab') {
                          setSelectedFoundation(buildSlabFoundationWithLayout(updatedFoundation, slabOptions));
                        } else {
                          setSelectedFoundation(updatedFoundation);
                          if (foundationType !== 'spread_footing') markAsCustom();
                        }
                      }
                    }}
                    inputProps={{ min: 0.5, max: 30, step: 0.1 }}
                    disabled={!selectedFoundation && foundationType !== 'ground_facing_slab'}
                  />
                </Grid>
                {(foundationType === 'spread_footing' || foundationType === 'ground_facing_slab') && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Slab Thickness (m)"
                      value={selectedFoundation?.dimensions?.H || ''}
                      onChange={(e) => {
                        if (selectedFoundation) {
                          const updatedFoundation = {
                            ...selectedFoundation,
                            dimensions: {
                              ...selectedFoundation.dimensions,
                              H: parseFlexibleFloat(e.target.value) || selectedFoundation.dimensions.H,
                            },
                          };
                          if (foundationType === 'ground_facing_slab') {
                            setSelectedFoundation(buildSlabFoundationWithLayout(updatedFoundation, slabOptions));
                          } else {
                            setSelectedFoundation(updatedFoundation);
                          }
                        }
                      }}
                      inputProps={{ min: 0.15, max: 1.0, step: 0.01 }}
                    />
                  </Grid>
                )}
                {(foundationType === 'spread_footing' || foundationType === 'ground_facing_slab') && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Concrete</InputLabel>
                      <Select
                        label="Concrete"
                        value={selectedFoundation?.concrete || (foundationType === 'ground_facing_slab' ? 'C30/37' : 'C25/30')}
                        onChange={(e) => {
                          if (!selectedFoundation) return;
                          setSelectedFoundation({ ...selectedFoundation, concrete: e.target.value });
                        }}
                      >
                        <MenuItem value="C25/30">C25/30</MenuItem>
                        <MenuItem value="C30/37">C30/37</MenuItem>
                        <MenuItem value="C35/45">C35/45</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {foundationType !== 'spread_footing' && foundationType !== 'ground_facing_slab' && (
                  <>
                    <Grid item xs={12} md={foundationType === 'custom' ? 4 : 5}>
                      <FormControl
                        fullWidth
                        disabled={foundationType === 'custom'}
                        error={false}
                        key={`foundation-select-${foundationType}`}
                      >
                        <InputLabel
                          id="foundation-select-label"
                          shrink
                        >
                          Standard Foundation
                        </InputLabel>
                        <Select
                          labelId="foundation-select-label"
                          label="Standard Foundation"
                          value={selectedFoundation?.code || ''}
                          displayEmpty
                          disabled={foundationType === 'custom'}
                          native
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            console.log('Native select onChange triggered with value:', selectedValue);
                            handleFoundationSelection(selectedValue);
                          }}
                        >
                          {foundationType === 'custom' ? (
                            <option value="CUSTOM-4PILE">
                              Custom Foundation - 4 piles in square pattern
                            </option>
                          ) : (
                            <>
                              <option value="">
                                {standardFoundations.length === 0
                                  ? 'Loading foundations...'
                                  : 'Select a foundation'}
                              </option>
                              {standardFoundations.length > 0 && (
                                standardFoundations.map(foundation => {
                                  if (!foundation || !foundation.code) {
                                    console.warn('Invalid foundation:', foundation);
                                    return null;
                                  }
                                  const dims = foundation.dimensions || { L: 0, W: 0, H: 0 };
                                  return (
                                    <option
                                      key={foundation.code}
                                      value={foundation.code}
                                    >
                                      {foundation.code} - {foundation.num_piles || 0} piles, {dims.L?.toFixed(1) || '0.0'}×{dims.W?.toFixed(1) || '0.0'}×{dims.H?.toFixed(1) || '0.0'}m
                                    </option>
                                  );
                                })
                              )}
                              {loadCustomFoundations().length > 0 && (
                                <>
                                  <optgroup label="Custom Foundations">
                                    {loadCustomFoundations()
                                      .filter(f => !foundationType || f.foundation_type === foundationType)
                                      .map(foundation => (
                                        <option
                                          key={foundation.code}
                                          value={foundation.code}
                                        >
                                          {foundation.customName || foundation.code} - {foundation.num_piles || foundation.layout?.length || 0} piles (Custom)
                                        </option>
                                      ))}
                                  </optgroup>
                                </>
                              )}
                            </>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {showAdvanced && foundationType !== 'spread_footing' && foundationType !== 'ground_facing_slab' && (
                      <Grid item xs={12} md={foundationType === 'custom' ? 4 : 2}>
                        <FormControl fullWidth>
                          <InputLabel id="ptl-class-label">PTL Class</InputLabel>
                          <Select
                            labelId="ptl-class-label"
                            label="PTL Class"
                            value={ptlClass}
                            onChange={(e) => {
                              const newPtlClass = parseInt(e.target.value);
                              setPtlClass(newPtlClass);
                              if (selectedFoundation) {
                                setSelectedFoundation({
                                  ...selectedFoundation,
                                  ptl_class: newPtlClass
                                });
                                if (foundationType !== 'custom') {
                                  markAsCustom(); // Changing PTL class makes it custom
                                }
                              }
                            }}
                            disabled={!selectedFoundation}
                          >
                            <MenuItem value={2}>PTL 2</MenuItem>
                            <MenuItem value={3}>PTL 3</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}
                {selectedFoundation && (
                  <Grid item xs={12}>
                    <Alert severity={isCustomFoundation ? "warning" : "info"}>
                      <Typography variant="body2">
                        <strong>Selected:</strong> {selectedFoundation.code}<br/>
                        {foundationType === 'spread_footing' ? (
                          <>
                            <strong>Concrete:</strong> {selectedFoundation.concrete} |
                            <strong>Reinforcement:</strong> {selectedFoundation.rebar}<br/>
                            <strong>Dimensions:</strong> {selectedFoundation.dimensions.L}m × {selectedFoundation.dimensions.W}m × {selectedFoundation.dimensions.H}m thickness<br/>
                            <strong>Foundation Type:</strong> Spread Footing (soil bearing)
                          </>
                        ) : foundationType === 'ground_facing_slab' ? (
                          <>
                            <strong>Concrete:</strong> {selectedFoundation.concrete}<br/>
                            <strong>Dimensions:</strong> {selectedFoundation.dimensions.L}m × {selectedFoundation.dimensions.W}m × {selectedFoundation.dimensions.H}m<br/>
                            <strong>Piles:</strong> {slabOptions.pile_grid?.enabled ? `${selectedFoundation.num_piles || 0} on regular grid` : 'None (soil bearing only)'}<br/>
                            <strong>Design:</strong> Finnish Eurocodes (SFS-EN + NAF) — preliminary slab check
                          </>
                        ) : (
                          <>
                            <strong>Concrete:</strong> {selectedFoundation.concrete} |
                            <strong>Reinforcement:</strong> {selectedFoundation.rebar} |
                            <strong>PTL Class:</strong> {selectedFoundation.ptl_class}<br/>
                            <strong>Dimensions:</strong> {selectedFoundation.dimensions.L}m × {selectedFoundation.dimensions.W}m × {selectedFoundation.dimensions.H}m height
                            {isCustomFoundation && (
                              <>
                                <br/>
                                <strong style={{ color: '#ed6c02' }}>⚠️ Modified Configuration</strong> - Custom pile layout and properties
                              </>
                            )}
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button size="small" variant="outlined" onClick={() => setShowAdvanced((v) => !v)}>
                  {showAdvanced ? 'Hide advanced inputs' : 'Show advanced inputs'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* Project Configuration */}
        <Grid item xs={12} md={foundationType === 'ground_facing_slab' ? 12 : 6}>
          <Card>
            <CardHeader title="Project Configuration" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Name"
                    value={projectData.name}
                    onChange={(e) => setProjectData({...projectData, name: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={projectData.description}
                    onChange={(e) => setProjectData({...projectData, description: e.target.value})}
                  />
                </Grid>
                {showAdvanced && foundationType !== 'ground_facing_slab' && (
                <>
                {(foundationType === 'column' || foundationType === 'wall' || foundationType === 'custom') && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={projectData.seismic_enabled}
                        onChange={(e) => setProjectData({...projectData, seismic_enabled: e.target.checked})}
                      />
                    }
                    label="Enable seismic analysis (EN 1998-1 / 1998-5)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={projectData.tension_resistance_enabled}
                        onChange={(e) => setProjectData({...projectData, tension_resistance_enabled: e.target.checked})}
                      />
                    }
                    label="Enable Pile Tension Resistance"
                  />
                </Grid>
                )}
                {(foundationType === 'column' || foundationType === 'wall' || foundationType === 'custom') && (
                <Grid item xs={12}>
                  <Collapse in={projectData.seismic_enabled}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                      Seismic parameters (Eurocode 8)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="a_g,R (g)"
                          value={projectData.seismic_params?.a_gR ?? 0.1}
                          onChange={(e) => setSeismicParam('a_gR', parseFlexibleFloat(e.target.value))}
                          helperText="From national annex"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Building height H (m)"
                          value={projectData.seismic_params?.building_height_m ?? 12}
                          onChange={(e) => setSeismicParam('building_height_m', parseFlexibleFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ground type</InputLabel>
                          <Select
                            value={projectData.seismic_params?.ground_type || 'auto'}
                            label="Ground type"
                            onChange={(e) => setSeismicParam('ground_type', e.target.value)}
                          >
                            <MenuItem value="auto">Auto (from soil)</MenuItem>
                            <MenuItem value="A">A</MenuItem>
                            <MenuItem value="B">B</MenuItem>
                            <MenuItem value="C">C</MenuItem>
                            <MenuItem value="D">D</MenuItem>
                            <MenuItem value="E">E</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Spectrum</InputLabel>
                          <Select
                            value={projectData.seismic_params?.spectrum_type || 'Type1'}
                            label="Spectrum"
                            onChange={(e) => setSeismicParam('spectrum_type', e.target.value)}
                          >
                            <MenuItem value="Type1">Type 1</MenuItem>
                            <MenuItem value="Type2">Type 2</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Importance</InputLabel>
                          <Select
                            value={projectData.seismic_params?.importance_class || 'II'}
                            label="Importance"
                            onChange={(e) => setSeismicParam('importance_class', e.target.value)}
                          >
                            <MenuItem value="I">I</MenuItem>
                            <MenuItem value="II">II</MenuItem>
                            <MenuItem value="III">III</MenuItem>
                            <MenuItem value="IV">IV</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Structural system</InputLabel>
                          <Select
                            value={projectData.seismic_params?.structural_system || 'other_DCL'}
                            label="Structural system"
                            onChange={(e) => setSeismicParam('structural_system', e.target.value)}
                          >
                            <MenuItem value="other_DCL">Other / DCL</MenuItem>
                            <MenuItem value="RC_frame_DCM">RC frame DCM</MenuItem>
                            <MenuItem value="RC_frame_DCH">RC frame DCH</MenuItem>
                            <MenuItem value="steel_frame_DCM">Steel frame DCM</MenuItem>
                            <MenuItem value="steel_frame_DCH">Steel frame DCH</MenuItem>
                            <MenuItem value="timber">Timber</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Behaviour factor q"
                          value={projectData.seismic_params?.behavior_factor_q ?? 1.5}
                          onChange={(e) => setSeismicParam('behavior_factor_q', parseFlexibleFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ductility class</InputLabel>
                          <Select
                            value={projectData.seismic_params?.ductility_class || 'DCL'}
                            label="Ductility class"
                            onChange={(e) => setSeismicParam('ductility_class', e.target.value)}
                          >
                            <MenuItem value="DCL">DCL</MenuItem>
                            <MenuItem value="DCM">DCM</MenuItem>
                            <MenuItem value="DCH">DCH</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="M_w earthquake"
                          value={projectData.seismic_params?.earthquake_magnitude_Mw ?? 6.5}
                          onChange={(e) => setSeismicParam('earthquake_magnitude_Mw', parseFlexibleFloat(e.target.value))}
                          helperText="Liquefaction MSF"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Overstrength Ω"
                          value={projectData.seismic_params?.structural_overstrength_omega ?? 1}
                          onChange={(e) => setSeismicParam('structural_overstrength_omega', parseFlexibleFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.enable_liquefaction_check !== false}
                              onChange={(e) => setSeismicParam('enable_liquefaction_check', e.target.checked)}
                            />
                          }
                          label="Liquefaction check"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.enable_kinematic_check !== false}
                              onChange={(e) => setSeismicParam('enable_kinematic_check', e.target.checked)}
                            />
                          }
                          label="Kinematic bending"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.enable_mononobe_okabe !== false}
                              onChange={(e) => setSeismicParam('enable_mononobe_okabe', e.target.checked)}
                            />
                          }
                          label="Mononobe–Okabe (embed.)"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.enable_capacity_design !== false}
                              onChange={(e) => setSeismicParam('enable_capacity_design', e.target.checked)}
                            />
                          }
                          label="Capacity design γRd·Ω"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.enable_distributed_check === true}
                              onChange={(e) => setSeismicParam('enable_distributed_check', e.target.checked)}
                            />
                          }
                          label="Distributed pile check (BEF + free-field u, experimental)"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Distributed solver nodes"
                          type="number"
                          size="small"
                          fullWidth
                          inputProps={{ min: 21, max: 501, step: 10 }}
                          value={projectData.seismic_params?.distributed_solver_nodes ?? 201}
                          onChange={(e) =>
                            setSeismicParam('distributed_solver_nodes', parseInt(e.target.value, 10) || 201)
                          }
                          disabled={!projectData.seismic_params?.enable_distributed_check}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={projectData.seismic_params?.distributed_include_p_delta === true}
                              onChange={(e) =>
                                setSeismicParam('distributed_include_p_delta', e.target.checked)
                              }
                              disabled
                            />
                          }
                          label="P–Δ in distributed solver (reserved)"
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
                )}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Embedment Depth (m)"
                    value={projectData.embedment_depth}
                    onChange={(e) => setProjectData({...projectData, embedment_depth: parseFlexibleFloat(e.target.value)})}
                    helperText="Depth to foundation top"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>
                {(foundationType === 'column' || foundationType === 'wall' || foundationType === 'custom') && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Pile Head Fixity Mode
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    color="primary"
                    size="small"
                    value={projectData.pile_head_fixity_mode || 'preset'}
                    onChange={(_, mode) => {
                      if (!mode) return;
                      setProjectData({ ...projectData, pile_head_fixity_mode: mode });
                    }}
                    sx={{ mb: 1, display: 'flex', flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="preset">Preset</ToggleButton>
                    <ToggleButton value="computed">Computed</ToggleButton>
                    <ToggleButton value="envelope">Envelope</ToggleButton>
                  </ToggleButtonGroup>

                  {(projectData.pile_head_fixity_mode || 'preset') === 'preset' && (
                    <FormControl fullWidth>
                      <InputLabel>Fixity preset</InputLabel>
                      <Select
                        label="Fixity preset"
                        value={projectData.pile_head_fixity_preset || 'pinned'}
                        onChange={(e) => setProjectData({ ...projectData, pile_head_fixity_preset: e.target.value })}
                      >
                        <MenuItem value="pinned">Pinned (0.0)</MenuItem>
                        <MenuItem value="partial">Partial (0.5)</MenuItem>
                        <MenuItem value="fixed">Fixed (1.0)</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {(projectData.pile_head_fixity_mode || 'preset') === 'computed' && (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cap embedment of pile head (m)"
                        value={projectData.pile_head_fixity_computed?.cap_embedment_m ?? 0}
                        inputProps={{ step: 0.05, min: 0 }}
                        onChange={(e) =>
                          setProjectData({
                            ...projectData,
                            pile_head_fixity_computed: {
                              ...(projectData.pile_head_fixity_computed || {}),
                              cap_embedment_m: parseFlexibleFloat(e.target.value),
                            },
                          })
                        }
                      />
                      <FormControl fullWidth>
                        <InputLabel>Top rebar development</InputLabel>
                        <Select
                          label="Top rebar development"
                          value={projectData.pile_head_fixity_computed?.top_rebar_development || 'none'}
                          onChange={(e) =>
                            setProjectData({
                              ...projectData,
                              pile_head_fixity_computed: {
                                ...(projectData.pile_head_fixity_computed || {}),
                                top_rebar_development: e.target.value,
                              },
                            })
                          }
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="partial">Partial</MenuItem>
                          <MenuItem value="full">Full</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cap thickness (m)"
                        value={projectData.pile_head_fixity_computed?.cap_thickness_m ?? 0}
                        inputProps={{ step: 0.05, min: 0 }}
                        onChange={(e) =>
                          setProjectData({
                            ...projectData,
                            pile_head_fixity_computed: {
                              ...(projectData.pile_head_fixity_computed || {}),
                              cap_thickness_m: parseFlexibleFloat(e.target.value),
                            },
                          })
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        Resolved fixity: {getComputedFixity().toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {(projectData.pile_head_fixity_mode || 'preset') === 'envelope' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Analysis runs at pinned (0.0) and fixed (1.0). Worst-case utilization is reported.
                    </Alert>
                  )}

                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => setShowAdvancedFixity((v) => !v)}>
                      {showAdvancedFixity ? 'Hide' : 'Show'} Advanced override
                    </Button>
                    <Collapse in={showAdvancedFixity}>
                      <Tooltip title="Advanced: numeric head fixity override">
                        <TextField
                          sx={{ mt: 1 }}
                          fullWidth
                          type="number"
                          label="Pile Head Fixity Override (0-1)"
                          value={projectData.pile_head_fixity}
                          onChange={(e) => {
                            const val = Math.max(0, Math.min(1, parseFlexibleFloat(e.target.value)));
                            setProjectData({ ...projectData, pile_head_fixity: val });
                          }}
                          helperText="Advanced override. Use only if calibrated from FEM or load tests."
                          inputProps={{ step: 0.1, min: 0, max: 1 }}
                        />
                      </Tooltip>
                    </Collapse>
                  </Box>
                </Grid>
                )}
                {showAdvanced && foundationType !== 'spread_footing' && (
                  <Grid item xs={12}>
                    <Tooltip title="Disabled by default per RIL 254 / EN 1997-1. Automatically disabled if any soil layer is marked as settling (downdrag).">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(projectData.allow_piled_soil_bearing) && !soilLayers.some((layer) => layer.is_settling)}
                            disabled={soilLayers.some((layer) => layer.is_settling)}
                            onChange={(e) => setProjectData({ ...projectData, allow_piled_soil_bearing: e.target.checked })}
                          />
                        }
                        label="Allow soil bearing support with piles (piled raft / non-settling prepared base)"
                      />
                    </Tooltip>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Embedded cap — passive earth pressure (Rankine, vertical face only)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Groundwater depth (m)"
                    value={projectData.groundwater_depth}
                    onChange={(e) => setProjectData({ ...projectData, groundwater_depth: e.target.value })}
                    helperText="Depth from ground surface to water table; leave 0 or empty if dry"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="passive-mob-label">Passive mobilisation</InputLabel>
                    <Select
                      labelId="passive-mob-label"
                      label="Passive mobilisation"
                      value={projectData.passive_mobilisation || 'hyperbolic'}
                      onChange={(e) => setProjectData({ ...projectData, passive_mobilisation: e.target.value })}
                    >
                      <Tooltip title="Assumes full passive resistance is mobilized at ULS without displacement reduction." placement="right">
                        <MenuItem value="full">Full (ULS, displacement-independent)</MenuItem>
                      </Tooltip>
                      <Tooltip title="Mobilizes passive pressure smoothly with cap translation (nonlinear hyperbolic curve)." placement="right">
                        <MenuItem value="hyperbolic">Hyperbolic vs cap translation</MenuItem>
                      </Tooltip>
                      <Tooltip title="Mobilizes passive pressure in two stages: initial stiffness then plateau at full passive resistance." placement="right">
                        <MenuItem value="bilinear">Bilinear vs cap translation</MenuItem>
                      </Tooltip>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Passive ref. displacement (m, optional)"
                    value={projectData.passive_ref_displacement_m}
                    onChange={(e) => setProjectData({ ...projectData, passive_ref_displacement_m: e.target.value })}
                    helperText="If empty, uses ratio × cap height below"
                    inputProps={{ step: 0.001, min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Passive ref. ratio × cap height"
                    value={projectData.passive_ref_ratio_h}
                    onChange={(e) => setProjectData({ ...projectData, passive_ref_ratio_h: parseFlexibleFloat(e.target.value) })}
                    helperText="Default 0.02 when ref. displacement not set"
                    inputProps={{ step: 0.005, min: 0.0001, max: 0.2 }}
                  />
                </Grid>
                {foundationType !== 'spread_footing' && foundationType !== 'ground_facing_slab' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Pile cap — column punching (EN 1992-1-1)
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(projectData.check_column_punching)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProjectData((prev) => ({
                                ...prev,
                                check_column_punching: checked,
                                ...(checked
                                  ? {
                                      column_side_x_m:
                                        prev.column_side_x_m > 0 ? prev.column_side_x_m : 0.4,
                                      column_side_y_m:
                                        prev.column_side_y_m > 0 ? prev.column_side_y_m : 0.4,
                                    }
                                  : {}),
                              }));
                            }}
                          />
                        }
                        label="Check column punching (downward shear at column perimeter)"
                      />
                    </Grid>
                    {projectData.check_column_punching && (
                      <>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Column width (m)"
                            value={projectData.column_side_x_m}
                            onChange={(e) =>
                              setProjectData({
                                ...projectData,
                                column_side_x_m: parseFlexibleFloat(e.target.value),
                              })
                            }
                            helperText="Plan dimension — shorter side if rectangular"
                            inputProps={{ step: 0.05, min: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Column depth (m)"
                            value={projectData.column_side_y_m}
                            onChange={(e) =>
                              setProjectData({
                                ...projectData,
                                column_side_y_m: parseFlexibleFloat(e.target.value),
                              })
                            }
                            helperText="Plan dimension — longer side if rectangular"
                            inputProps={{ step: 0.05, min: 0.01 }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {foundationType === 'ground_facing_slab' && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Ground-facing Slab Options" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Characteristic loads. Self-weight is included in every scenario. Uniform live, point load, and line load are checked separately.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Uniform live load</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={slabOptions.loads?.uniform_enabled !== false}
                          onChange={(e) => setSlabOptions({
                            ...slabOptions,
                            loads: { ...slabOptions.loads, uniform_enabled: e.target.checked },
                          })}
                        />
                      }
                      label="Analyse uniform live load"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Uniform live load (kN/m²)"
                      value={slabOptions.loads?.uniform_live_kpa ?? 2}
                      disabled={slabOptions.loads?.uniform_enabled === false}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        loads: { ...slabOptions.loads, uniform_live_kpa: parseFlexibleFloat(e.target.value) },
                      })}
                      helperText="Characteristic imposed load. ULS applies γQ."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Collapse in={showAdvanced}>
                      <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Concentrated load (patch)</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={slabOptions.loads?.concentrated?.enabled !== false}
                          onChange={(e) => setSlabOptions({
                            ...slabOptions,
                            loads: {
                              ...slabOptions.loads,
                              concentrated: { ...slabOptions.loads?.concentrated, enabled: e.target.checked },
                            },
                          })}
                        />
                      }
                      label="Analyse concentrated patch"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Force (kN)"
                      value={slabOptions.loads?.concentrated?.force_kn ?? 20}
                      disabled={slabOptions.loads?.concentrated?.enabled === false}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        loads: {
                          ...slabOptions.loads,
                          concentrated: { ...slabOptions.loads?.concentrated, force_kn: parseFlexibleFloat(e.target.value) },
                        },
                      })}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Patch (mm)"
                      value={slabOptions.loads?.concentrated?.patch_mm ?? 200}
                      disabled={slabOptions.loads?.concentrated?.enabled === false}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        loads: {
                          ...slabOptions.loads,
                          concentrated: { ...slabOptions.loads?.concentrated, patch_mm: parseFlexibleFloat(e.target.value) },
                        },
                      })}
                      helperText="Square patch, e.g. 200"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Edge inset (m)"
                      value={slabOptions.loads?.edge_inset_m ?? 0.3}
                      disabled={slabOptions.loads?.concentrated?.enabled === false}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        loads: { ...slabOptions.loads, edge_inset_m: parseFlexibleFloat(e.target.value) },
                      })}
                      helperText="Free edge / corner check offset"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Line load (edge socle / light wall)</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(slabOptions.loads?.line?.enabled)}
                          onChange={(e) => setSlabOptions({
                            ...slabOptions,
                            loads: {
                              ...slabOptions.loads,
                              line: { ...slabOptions.loads?.line, enabled: e.target.checked },
                            },
                          })}
                        />
                      }
                      label="Analyse edge line load"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Line load (kN/m)"
                      value={slabOptions.loads?.line?.intensity_kn_per_m ?? 5}
                      disabled={!slabOptions.loads?.line?.enabled}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        loads: {
                          ...slabOptions.loads,
                          line: { ...slabOptions.loads?.line, intensity_kn_per_m: parseFlexibleFloat(e.target.value) },
                        },
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!slabOptions.loads?.line?.enabled}>
                      <InputLabel>Edges with line load</InputLabel>
                      <Select
                        multiple
                        label="Edges with line load"
                        value={slabOptions.loads?.line?.edges || ['pos_x']}
                        onChange={(e) => setSlabOptions({
                          ...slabOptions,
                          loads: {
                            ...slabOptions.loads,
                            line: { ...slabOptions.loads?.line, edges: e.target.value },
                          },
                        })}
                        renderValue={(selected) => (selected || []).join(', ')}
                      >
                        <MenuItem value="pos_x">+X edge (long side)</MenuItem>
                        <MenuItem value="neg_x">-X edge (long side)</MenuItem>
                        <MenuItem value="pos_y">+Y edge (short side)</MenuItem>
                        <MenuItem value="neg_y">-Y edge (short side)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Analysis mesh &amp; durability</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Cover (m)"
                      value={slabOptions.cover_m}
                      onChange={(e) => setSlabOptions({ ...slabOptions, cover_m: parseFlexibleFloat(e.target.value) })}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Mesh size (m)"
                      value={slabOptions.mesh?.spacing_m ?? 1}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        mesh: { spacing_m: parseFlexibleFloat(e.target.value) || 1 },
                      })}
                      inputProps={{ step: 0.1, min: 0.2 }}
                      helperText="Target element size"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Support layers below slab</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={slabOptions.gravel?.enabled !== false}
                          onChange={(e) => setSlabOptions({ ...slabOptions, gravel: { ...slabOptions.gravel, enabled: e.target.checked } })}
                        />
                      }
                      label="Gravel layer"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Gravel (m)"
                      value={slabOptions.gravel?.thickness_m ?? 0.3}
                      onChange={(e) => setSlabOptions({ ...slabOptions, gravel: { ...slabOptions.gravel, thickness_m: parseFlexibleFloat(e.target.value) } })}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(slabOptions.xps?.enabled)}
                          onChange={(e) => setSlabOptions({ ...slabOptions, xps: { ...slabOptions.xps, enabled: e.target.checked } })}
                        />
                      }
                      label="XPS insulation"
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="XPS (m)"
                      value={slabOptions.xps?.thickness_m ?? 0.1}
                      disabled={!slabOptions.xps?.enabled}
                      onChange={(e) => setSlabOptions({ ...slabOptions, xps: { ...slabOptions.xps, thickness_m: parseFlexibleFloat(e.target.value) } })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(slabOptions.pile_grid?.enabled)}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            const next = { ...slabOptions, pile_grid: { ...slabOptions.pile_grid, enabled } };
                            setSlabOptions(next);
                            if (selectedFoundation) {
                              setSelectedFoundation(buildSlabFoundationWithLayout(selectedFoundation, next));
                            }
                          }}
                        />
                      }
                      label="Single piles on regular grid"
                    />
                  </Grid>
                  {slabOptions.pile_grid?.enabled && (
                    <>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Spacing X (m)" value={slabOptions.pile_grid.spacing_x_m}
                          onChange={(e) => {
                            const next = { ...slabOptions, pile_grid: { ...slabOptions.pile_grid, spacing_x_m: parseFlexibleFloat(e.target.value) } };
                            setSlabOptions(next);
                            if (selectedFoundation) setSelectedFoundation(buildSlabFoundationWithLayout(selectedFoundation, next));
                          }} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Spacing Y (m)" value={slabOptions.pile_grid.spacing_y_m}
                          onChange={(e) => {
                            const next = { ...slabOptions, pile_grid: { ...slabOptions.pile_grid, spacing_y_m: parseFlexibleFloat(e.target.value) } };
                            setSlabOptions(next);
                            if (selectedFoundation) setSelectedFoundation(buildSlabFoundationWithLayout(selectedFoundation, next));
                          }} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Edge offset X (m)" value={slabOptions.pile_grid.offset_x_m}
                          onChange={(e) => {
                            const next = { ...slabOptions, pile_grid: { ...slabOptions.pile_grid, offset_x_m: parseFlexibleFloat(e.target.value) } };
                            setSlabOptions(next);
                            if (selectedFoundation) setSelectedFoundation(buildSlabFoundationWithLayout(selectedFoundation, next));
                          }} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Edge offset Y (m)" value={slabOptions.pile_grid.offset_y_m}
                          onChange={(e) => {
                            const next = { ...slabOptions, pile_grid: { ...slabOptions.pile_grid, offset_y_m: parseFlexibleFloat(e.target.value) } };
                            setSlabOptions(next);
                            if (selectedFoundation) setSelectedFoundation(buildSlabFoundationWithLayout(selectedFoundation, next));
                          }} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Pile size (mm)" value={slabOptions.pile_grid.pile_size_mm}
                          onChange={(e) => setSlabOptions({ ...slabOptions, pile_grid: { ...slabOptions.pile_grid, pile_size_mm: parseInt(e.target.value, 10) || 250 } })} />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Partial factors (SFS-EN 1990 + NAF)</Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="γG"
                      value={slabOptions.design_basis?.gamma_g ?? 1.35}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        design_basis: { ...slabOptions.design_basis, gamma_g: parseFlexibleFloat(e.target.value) || 1.35 },
                      })}
                      inputProps={{ step: 0.05, min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="γQ"
                      value={slabOptions.design_basis?.gamma_q ?? 1.5}
                      onChange={(e) => setSlabOptions({
                        ...slabOptions,
                        design_basis: { ...slabOptions.design_basis, gamma_q: parseFlexibleFloat(e.target.value) || 1.5 },
                      })}
                      inputProps={{ step: 0.05, min: 1 }}
                    />
                  </Grid>
                      </Grid>
                    </Collapse>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Custom Foundation Name (only shown when custom) */}
        {isCustomFoundation && selectedFoundation && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <TextField
                  fullWidth
                  label="Custom Foundation Name"
                  value={customFoundationName}
                  onChange={(e) => {
                    setCustomFoundationName(e.target.value);
                    if (selectedFoundation) {
                      setSelectedFoundation({
                        ...selectedFoundation,
                        customName: e.target.value,
                        customCode: e.target.value || generateCustomFoundationName(selectedFoundation)
                      });
                    }
                  }}
                  helperText="This name is for display/storage only, not used in calculations"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Foundation Layout Viewer */}
        {selectedFoundation && foundationType !== 'ground_facing_slab' && (foundationType === 'custom' || showAdvanced) && (
          <Grid item xs={12}>
            <FoundationLayoutViewer
              foundation={{
                ...selectedFoundation,
                pile_length: pileLength,
                customName: customFoundationName,
                ptl_class: ptlClass || selectedFoundation.ptl_class || 2,
                soil_layers: soilLayers,
                embedment_depth: projectData.embedment_depth || 0,
              }}
              isEditable={true} // Always editable - edits will trigger custom mode
              onLayoutChange={(updatedFoundation) => {
                // Always update foundation when layout changes
                // This ensures custom mode is triggered
                console.log('onLayoutChange called with updatedFoundation:', updatedFoundation);
                console.log('updatedFoundation.pileData:', updatedFoundation.pileData);
                console.log('updatedFoundation.layout:', updatedFoundation.layout);
                // Update ref with latest pileData for synchronous access
                if (updatedFoundation.pileData) {
                  latestPileDataRef.current = updatedFoundation.pileData;
                  console.log('Updated latestPileDataRef.current:', latestPileDataRef.current);
                }
                setSelectedFoundation(updatedFoundation);
                markAsCustom();
              }}
            />
          </Grid>
        )}

        {selectedFoundation && foundationType === 'ground_facing_slab' && slabOptions.pile_grid?.enabled && (
          <Grid item xs={12}>
            <FoundationLayoutViewer
              foundation={{
                ...buildSlabFoundationWithLayout(selectedFoundation, slabOptions),
                pile_length: pileLength,
                soil_layers: soilLayers,
              }}
              isEditable={false}
            />
          </Grid>
        )}

        {/* Loads — pile caps and spread footings. Ground-facing slab uses its own load cases. */}
        {foundationType !== 'ground_facing_slab' && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Design Loads" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {foundationType === 'spread_footing'
                  ? 'Characteristic vertical load. Open advanced inputs for horizontal loads and moments.'
                  : 'Enter loads in kN and kNm (positive = compression/downward).'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vertical Load (kN)"
                    value={loads.vertical_load}
                    onChange={(e) => setLoads({...loads, vertical_load: parseFlexibleFloat(e.target.value)})}
                  />
                </Grid>
                {foundationType === 'spread_footing' && !showAdvanced && (Math.abs(loads.horizontal_load_x) + Math.abs(loads.horizontal_load_y) + Math.abs(loads.moment_x) + Math.abs(loads.moment_y) + Math.abs(loads.moment_z) > 0) && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Horizontal loads or moments are set. Open advanced inputs to view them. They are included in the analysis.
                    </Alert>
                  </Grid>
                )}
                {(foundationType !== 'spread_footing' || showAdvanced) && (
                <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Horizontal Y (kN)"
                    value={loads.horizontal_load_y}
                    onChange={(e) => setLoads({...loads, horizontal_load_y: parseFlexibleFloat(e.target.value)})}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Horizontal X (kN)"
                    value={loads.horizontal_load_x}
                    onChange={(e) => setLoads({...loads, horizontal_load_x: parseFlexibleFloat(e.target.value)})}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Moment X (Mx) (kNm)"
                    value={loads.moment_x}
                    onChange={(e) => setLoads({...loads, moment_x: parseFlexibleFloat(e.target.value)})}
                    helperText="Rotation around X"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Moment Y (My) (kNm)"
                    value={loads.moment_y}
                    onChange={(e) => setLoads({...loads, moment_y: parseFlexibleFloat(e.target.value)})}
                    helperText="Rotation around Y"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Moment Z (Mz) (kNm)"
                    value={loads.moment_z}
                    onChange={(e) => setLoads({...loads, moment_z: parseFlexibleFloat(e.target.value)})}
                    helperText="Torsion around Z"
                  />
                </Grid>
                </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Soil Profile */}
        <Grid item xs={12} md={foundationType === 'ground_facing_slab' ? 12 : 6}>
          <Card>
            <CardHeader
              title="Soil Profile"
              action={
                <Button size="small" onClick={addSoilLayer}>
                  Add Layer
                </Button>
              }
            />
            <CardContent>
              {soilLayers.map((layer, index) => {
                const soilTypeLabel = soilTypes.find(t => t.value === layer.soil_type)?.label || 'Unknown Soil';
                return (
                  <div key={index}>
                    <Typography variant="subtitle2" gutterBottom>
                      Layer {index + 1} {index === 0 ? '(Top Soil)' : `(Below Layer ${index})`} - {soilTypeLabel}
                    </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Soil Type</InputLabel>
                        <Select
                          value={layer.soil_type}
                          onChange={(e) => updateSoilLayer(index, 'soil_type', e.target.value)}
                        >
                          {soilTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Top (m)"
                        value={layer.top_depth}
                        onChange={(e) => updateSoilLayer(index, 'top_depth', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Bottom (m)"
                        value={layer.bottom_depth}
                        onChange={(e) => updateSoilLayer(index, 'bottom_depth', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Tooltip title={renderParameterTooltip('gamma')} arrow placement="top">
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="γ (kN/m³)"
                          value={layer.unit_weight}
                          onChange={(e) => updateSoilLayer(index, 'unit_weight', e.target.value)}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Tooltip title={renderParameterTooltip('cu')} arrow placement="top">
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="cᵤ (kPa)"
                          value={layer.cohesion}
                          onChange={(e) => updateSoilLayer(index, 'cohesion', e.target.value)}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Tooltip title={renderParameterTooltip('phi')} arrow placement="top">
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="φ (°)"
                          value={layer.friction_angle}
                          onChange={(e) => updateSoilLayer(index, 'friction_angle', e.target.value)}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Tooltip title={renderParameterTooltip('E_modulus')} arrow placement="top">
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="E (MPa)"
                          value={layer.E_modulus || layer.modulus || ''}
                          onChange={(e) => updateSoilLayer(index, 'E_modulus', e.target.value)}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <Tooltip
                        title="If checked, this layer is treated as settling (e.g. fill or soft clay). Used for simplified downdrag: neutral plane at the bottom of the lowest flagged layer."
                        arrow
                        placement="top"
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={!!layer.is_settling}
                              onChange={(e) => updateSoilLayer(index, 'is_settling', e.target.checked)}
                            />
                          }
                          label="Settling layer (downdrag)"
                        />
                      </Tooltip>
                    </Grid>
                    {projectData.seismic_enabled && (
                      <>
                        <Grid item xs={4} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="N60 (SPT)"
                            value={layer.N60 ?? ''}
                            onChange={(e) => updateSoilLayer(index, 'N60', e.target.value)}
                            helperText="Liquefaction"
                          />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Fines %"
                            value={layer.fines_content_pct ?? ''}
                            onChange={(e) => updateSoilLayer(index, 'fines_content_pct', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={4} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!layer.is_saturated}
                                onChange={(e) => updateSoilLayer(index, 'is_saturated', e.target.checked)}
                              />
                            }
                            label="Saturated (below GWT)"
                          />
                        </Grid>
                      </>
                    )}
                    {soilLayers.length > 1 && (
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeSoilLayer(index)}
                        >
                          Remove Layer
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                  {index < soilLayers.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => insertSoilLayer(index)}>
                        Add layer between
                      </Button>
                    </Box>
                  )}
                </div>
              );
            })}
          </CardContent>
          </Card>
        </Grid>

        {/* Export */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Export" />
            <CardContent>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportCSV}
                disabled={!results}
              >
                Export CSV
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Analysis Button */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
                onClick={() => {
                  console.log('=== RUN FOUNDATION ANALYSIS BUTTON CLICKED ===');
                  console.log('Current state:');
                  console.log('- selectedFoundation:', selectedFoundation);
                  console.log('- foundationType:', foundationType);
                  console.log('- pileLength:', pileLength);
                  console.log('- soilLayers length:', soilLayers.length);
                  console.log('- loading:', loading);
                  console.log('- Calling handleRunAnalysis...');
                  handleRunAnalysis();
                }}
                disabled={loading}
                sx={{ minWidth: 200, py: 1.5 }}
              >
                {loading ? 'Running Analysis...' : 'Run Foundation Analysis'}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    Please fix the following issues:
                    {'\n• ' + error}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default FoundationInputForm;
